<script setup>
import { displayGameName } from '../../../shared/game-display-name.mjs'
import { createSocketRecovery } from '../services/socket-recovery.mjs'
/**
 * 牌九下注台。
 *
 * 全部逻辑逐条对齐旧版发布包 web/19.0d8589924b85d555a0a0.js 里的 app-paijiu
 * 组件（类 x、模板工厂 Cl、样式 J），接口地址取自 web/main.c9708dd9c98f419ca4a6.js
 * 的 GameApi / MemberApi / ConfigApi：
 *
 *   getGameState(id)   → POST /game/state      {game:id}
 *   getLotteryList(p)  → POST /game/history    p
 *   getGameOdds(id)    → POST /game/odds       {game:id}
 *   getStatistic(...)  → POST /game/statistic  {game,game_no,member,page,pagesize}
 *   getTodayBill(p)    → POST /game/bet/today  p
 *   bet(...)           → POST /game/bet/bet    {game,game_no,play,money,content}
 *   getUserMoney()     → GET  /user/money
 *   getConfig(name)    → POST /config/detail   {name}
 *
 * 所有 game 参数一律传 game_id（数字），与旧版一致 —— /game/bet/today 等接口在
 * 后端是 where('game_id', $game) 硬查询，传 game_name 会永远查不到数据。
 */
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiConfig, authStorage, request } from '../services/api'
import { orderPeriodMessages } from '../services/paijiu-chat'
import BetDetailTime from '../components/BetDetailTime.vue'
import PaijiuStatisticModal from '../components/PaijiuStatisticModal.vue'
import PaijiuBillModal from '../components/PaijiuBillModal.vue'
import PaijiuBetListModal from '../components/PaijiuBetListModal.vue'
import PaijiuHistoryModal from '../components/PaijiuHistoryModal.vue'
import PaijiuMoneyModal from '../components/PaijiuMoneyModal.vue'
import ChatFollowBetModal from '../components/ChatFollowBetModal.vue'
import { useChatFollowBet } from '../services/chat-follow-bet'

const route = useRoute()
const router = useRouter()

// ---------------------------------------------------------------------------
// 旧版构造函数里的初始状态，逐字对应
// ---------------------------------------------------------------------------

// 旧版 this.game
const game = ref({
  game_id: 0,
  game_title: '',
  game_name: '',
  game_template: '',
  game_close_time: 0,
  game_lottery_time: 0,
  game_rule: '',
  game_chat: 1,
  game_state: { game_no: '', lottery_state: 0, open_time: 0, close_time: 0, lottery_time: 0 },
})
// 旧版把 game_state 直接挂在 game 上，这里单独取一个 ref 方便每秒递减
const gameState = ref({ game_no: '', lottery_state: 0, open_time: 0, close_time: 0, lottery_time: 0 })
// 旧版 this.user = {username, nickname, balance, frozen}
const user = ref(null)
// 当前最后选择的筹码，仅用于高亮；下注金额在点击「确定」前保存在 pendingBets 中。
const betMoney = ref(0)
// 操作栏中的自定义下注金额；按回车后加入当前选中的门位。
const customBetAmount = ref('')
// 旧版 this.gameCloseState：三个时间全部 < 0 时盖上「未开盘」遮罩
const gameCloseState = ref(false)
// 旧版 this.lottery：全局单个标志。封盘遮罩条件是 close && !lottery，牌面条件就是它
const lottery = ref(false)
// 旧版 this.lotteryGameNo = "0"，header 的期号读它（不是 game_state.game_no）
const lotteryGameNo = ref('0')
// 旧版 this.lotteryNumbers 初始就是十个 "08"
const lotteryNumbers = ref(['08', '08', '08', '08', '08', '08', '08', '08', '08', '08'])
// 旧版 this.showMenu = {show, x, y}，模板只用到 y（[style.top]）
const showMenu = ref({ show: false, x: '0px', y: '0px' })
// 旧版 this.lottery_animation_time = 5
const lotteryAnimationTime = ref(5)
// 旧版 this.today_bill。bet_money_uncheck（未结金额）是聊天室金额条要用的，
// 旧版大吃小 chunk 29 的 today_bill 初始值里同样带这个键。
const todayBill = ref({ bet_money: 0, bet_money_win: 0, bet_money_pl: 0, bet_money_back: 0, bet_money_valid: 0, bet_money_uncheck: 0 })
// 旧版 this.showService / this.service_wechat_pic
const showService = ref(false)
const serviceWechatPic = ref('')
// 旧版 this.audio.play（其余字段是音效路径，见 audioSources）
const audioOn = ref(false)
// 旧版 this.online（WebSocket connect / close 消息推送的在线人数）
const online = ref(0)
// 旧版 this.playList = getOdds() 的返回值，getPlay() 从它里面按 play_name 找玩法
const playList = ref([])
// 旧版 appService.showToast
const toastMessage = ref('')

/**
 * 旧版 betItems = {a:new k, b:new k, c:new k, d:new k, e:new k}。
 * 类 k 的初始值：play 全 0/空、lottery {item:[0,0],sum:0,rank:0}、
 * bet {total:0,member:0,member_win:0,lists:[]}、close:true（默认封盘）。
 *
 * 声明顺序 a,b,c,d,e 是决定性的：setLottery 用 Object.keys(betItems)[i] 去接
 * format_lottery_numbers.result[i]，后端 Ozxpj::lotteryResult 最后按 key 升序
 * 还原过，所以 result[i] 一定对应 gateOrder[i]。
 */
const gateOrder = ['a', 'b', 'c', 'd', 'e']
// 台面显示顺序不同：上排 a/b/c（标题 1/2/3），下排先 e（标题 5）再 d（标题 4）
const firstRow = ['a', 'b', 'c']
const secondRow = ['e', 'd']
const gateLabel = { a: 1, b: 2, c: 3, d: 4, e: 5 }

function createBetItem() {
  return {
    play: { play_id: 0, play_name: '', play_title: '', bet_odds: '', bet_content: '', bet_max_all: 0, bet_max_all_member: 0, bet_max_once: 0, bet_min: 0 },
    lottery: { item: [0, 0], sum: 0, rank: 0 },
    bet: { total: 0, member: 0, member_win: 0, lists: [] },
    close: true,
  }
}
const betItems = ref(Object.fromEntries(gateOrder.map((key) => [key, createBetItem()])))
// 当前期全部玩家在五个门的下注合计，下注、撤单和新一期清盘时会自动同步。
const currentBetTotal = computed(() => gateOrder.reduce((total, key) => total + Number(betItems.value[key].bet.total || 0), 0))
const selectedGate = ref(null)
const selectedGatePoint = ref(null)
const pendingBets = ref(Object.fromEntries(gateOrder.map((key) => [key, []])))
const isConfirmingBets = ref(false)
const pendingBetTotal = computed(() => gateOrder.reduce((total, key) => total + pendingBets.value[key].reduce((sum, chip) => sum + chip.money, 0), 0))
const bettingOpen = computed(() => Number(gameState.value.close_time) > 0)

function pendingBetAmount(key) {
  return pendingBets.value[key].reduce((total, chip) => total + chip.money, 0)
}

function resetPendingBets() {
  pendingBets.value = Object.fromEntries(gateOrder.map((key) => [key, []]))
  selectedGate.value = null
  selectedGatePoint.value = null
  betMoney.value = 0
  customBetAmount.value = ''
}

// 旧版 audio 对象里的五个音效，文件已从 web/assets/audio 拷到 public/legacy/audio
const audioSources = {
  cash_handle: '/legacy/audio/cash_handle.mp3',
  cash_cancel: '/legacy/audio/cash_cancel.mp3',
  recharge_handle: '/legacy/audio/recharge_handle.mp3',
  lottery: '/legacy/audio/lottery.mp3',
  bet: '/legacy/audio/bet.mp3',
}

// 旧版 this.state / this.lottery_animation_state / this.leave / this.inTimestamp
let stateTimer
let lotteryAnimTimer
let toastTimer
let pageLeft = false
let enteredAt = 0
// 旧版 this.webSocket / this.reConnectNumber
let socket
let socketRetryTimer
let reConnectNumber = 0
const socketRecovery = createSocketRecovery({ connect: () => connectWsServer(true),
  refresh: () => Promise.allSettled([getLastLottery(), getState(), getOdds(), getUserMoney(), getStatistic(), getTodayBill()]),
  isStopped: () => pageLeft })
// 旧版 chatCashIds / chatRechargeIds：localStorage 去重，避免重复提示
let handledCashIds = []
let handledRechargeIds = []
// 旧版重连 10 次后弹的 alert
const showReconnectAlert = ref(false)
// 报表弹窗：旧版 statisticModal(l)，l=true 传 game_no=-1（上一期），false 传当期
const statisticModal = ref(null)
// 旧版 ruleModal：把 game_rule 弹出来
const showRule = ref(false)
// 旧版 billModal：app-bill 全屏 modal（不是 /user/finance 账务流水）
const showBill = ref(false)
// 旧版 betModal：app-bet 全屏 modal（不是 /user/bets 游戏记录页）
const showBetList = ref(false)
// 旧版 historyModal：app-lottery 全屏 modal（不是 /game/:id/history 开奖历史页）
const showHistory = ref(false)
// 旧版 moneyModal(type)：app-money 全屏 modal，type 1 充值 / 2 提现（不是 /user/money/:type 页）
const moneyModal = ref(null)
// 牌九台面门位实时下注明细弹窗；只在本期未封盘时可打开。
const gateDetail = ref(null)
const gateDetailLoading = ref(false)
const gateDetailError = ref('')

// ---------------------------------------------------------------------------
// 聊天室（旧版牌九没有这个功能，属新增）
//
// 后端零改动：聊天室消息全部在通用层 application/common/logic/Game.php 里推送，
// type / game 都是动态取的，所以牌九和大吃小收到的是同一套消息结构：
//   collect     Game.php:167  开奖公告
//   statistic   Game.php:222  开奖报表
//   bet         Game.php:284  单注（牌九走 /game/bet/bet，只会推这个）
//   bet_batch   Game.php:346  批量注（牌九不会产生，保留兜底）
//   bet_cancel  Game.php:406  撤单
//   lottery     abstracts/Game.php:769 中奖通知
//   message     默认分支，用户聊天（WS 下发的 type 是 "chat"）
// GameMsg 表用 msg_game 做游戏隔离，所以牌九房间只收牌九的消息。
//
// UI 逐条对齐大吃小 PjxBetPage.vue（旧版依据 web/29.42d9f906f231ff156120.js
// 的 app-pjx 组件），差异只有两处：
//   1. tab 栏左边的标题从「大吃小」改成「牌九」
//   2. 开奖公告卡片按牌九的五门两张牌重排（见 chatCollectGates）
// ---------------------------------------------------------------------------

// 旧版 this.playType：'play' 牌桌 / 'chat' 聊天室
const playType = ref('play')
// 旧版 this.chatMessages / chatMessagesMaxLength = 100
const chatMessages = ref([])
const orderedChatMessages = computed(() => orderPeriodMessages(chatMessages.value))
const chatContent = ref('')
// 旧版 this.chatNewCount：不在聊天室时累计未读。按既定方案不在笑脸按钮上叠红圈，
// 只显示在 tab 栏「聊天室」右边的徽章里。
const chatNewCount = ref(0)
const chatScroll = ref(null)
// 旧版 this.chatShow 有 lottery/online/offline/bet_me/bet_other/collect/statistic
// 七个键，但界面上只暴露后四个筛选按钮，这里只保留被用到的四个。
const chatShow = ref({ bet_me: true, bet_other: true, collect: true, statistic: true, close_check: true })
// 旧版撤单前的「请确认」弹窗
const cancelConfirmTarget = ref(null)
// 旧版 this.lastTimestamp：两条消息间隔 >= 60 秒时插一条时间分隔
let lastChatTimestamp = 0

// 大吃小金额条用的三个数值（旧版 .account-strip）。bet_money_uncheck 是
// /game/bet/today 返回的未结金额，旧版 today_bill 里就有这个字段。
const availableBalance = computed(() => Number(user.value?.balance ?? user.value?.money ?? 0))
const unsettledAmount = computed(() => Number(todayBill.value?.bet_money_uncheck ?? 0))

// 旧版第一个 tab 只在切到聊天室、且尚未封盘时才显示倒计时：
// *ngIf="game.game_state.close_time >= 0 && playType == 'chat'"
const showTabCountdown = computed(() => Number(gameState.value.close_time) >= 0 && playType.value === 'chat')

/**
 * 后台游戏列表里的「聊天室」开关（game_chat），牌九已启用。
 * 旧版依据：chunk 24/26/27/28/29/30 的模板里都有 *ngIf="game.game_chat"。
 *
 * 刻意用 Number() 比较而不是直接判真假 —— game_chat 是 tinyint，接口若把它
 * 序列化成字符串 '0'，直接 v-if="game.game_chat" 会因为 '0' 是 truthy 而误判成开启。
 * 未加载到 game 之前按旧版构造函数的初始值 game_chat:1 处理，默认显示。
 */
const chatEnabled = computed(() => Number(game.value?.game_chat ?? 1) !== 0)

// 号码球配色直接复用大吃小的 palette（旧版 .olive/.blue/... 十个色号）
const palette = { 1: 'olive', 2: 'blue', 3: 'black', 4: 'orange', 5: 'teal', 6: 'purple', 7: 'gray', 8: 'red', 9: 'brown', 10: 'green' }
function color(number) { return palette[Number(number)] || 'gray' }

// 开奖公告卡片的门号标题：牌九是一~五门
const gateCnLabel = ['一', '二', '三', '四', '五']

const gameId = computed(() => game.value.game_id || route.params.game)
// 澳洲幸运5保留牌九牌桌与聊天室，仅开奖号码是五个 0-9 位置号，不使用牌面图片。
const isOzbPj = computed(() => String(game.value?.game_name || '').toLowerCase() === 'ozbpj')
// 旧版模板 node 47/49：lottery_time > 0 显示秒数，否则显示「开奖中」
const showCountdown = computed(() => Number(gameState.value.lottery_time) > 0)
// 旧版 ngClass{red: game_state.lottery_time <= game.game_lottery_time}
const countdownRed = computed(() => Number(gameState.value.lottery_time) <= Number(game.value.game_lottery_time || 0))
const todayProfit = computed(() => Number(todayBill.value?.bet_money_pl || 0))

// 旧版模板用 Angular DecimalPipe，默认 locale en-US，所以带千位分隔符。
// '.2-2' → 固定两位小数；'0.0' → 不留小数。
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const decimal0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }
function fmt0(value) { return decimal0.format(Number(value || 0)) }
function ozbpjLotteryTile(value) {
  const number = Number(value)
  const file = Number.isInteger(number) && number >= 0 && number <= 9
    ? (number === 0 ? '0' : String(number).padStart(2, '0'))
    : '0'
  return `/legacy/images/game/paijiu/lottery/${file}.png`
}

// 头像相对路径挂在后端站点根上，从已配置的 apiServer 推导出源站，避免写死域名。
// 与 SaicheBetPage / PjxBetPage / FantanBetPage 的 chatAvatar 保持一致。
const assetOrigin = (() => {
  try { return new URL(apiConfig.apiServer).origin } catch (_) { return '' }
})()
/** 会员头像：旧版 API 内置值是 /assets/userface.png，本地没有该文件，回退到打包的默认头像 */
const userAvatar = computed(() => {
  const value = user.value?.avatar || user.value?.member_avatar
  if (!value || value === '/assets/userface.png') return '/legacy/images/userface-default.png'
  if (/^(?:https?:|data:|blob:)/i.test(value)) return value
  return `${assetOrigin}${value.startsWith('/') ? value : `/${value}`}`
})
function onAvatarError(event) { event.target.onerror = null; event.target.src = '/legacy/images/userface-default.png' }

/** 旧版 appService.showToast */
function showToast(message) {
  toastMessage.value = message
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => { toastMessage.value = '' }, 3000)
}

/** 旧版 playAudio(l)：audio.play 关闭时什么都不做 */
function playAudio(name) {
  if (!audioOn.value) return
  const source = audioSources[name]
  if (!source) return
  try {
    const element = new Audio(source)
    element.volume = 1
    void element.play().catch(() => { /* 浏览器未授权自动播放时忽略 */ })
  } catch (_) { /* 音频不可用时不影响下注 */ }
}

/** 旧版 audio 开关点击：切换后写回 localStorage("game_audio_"+game_id) */
function toggleAudio() {
  audioOn.value = !audioOn.value
  try { localStorage.setItem(`game_audio_${gameId.value}`, String(audioOn.value)) } catch (_) { /* 存储不可用 */ }
}

/** 旧版 showMenuEvent(l)：show 取反，y = 点击位置 + 20px */
function showMenuEvent(event) {
  showMenu.value = {
    show: !showMenu.value.show,
    x: `${event.x - 25}px`,
    y: `${event.y + 20}px`,
  }
}

/** 旧版 getPlay(l)：在 playList 里按 play_name 找玩法，找不到返回 false */
function getPlay(playName) {
  if (typeof playName === 'object') return playName
  for (const type of playList.value) {
    for (const play of (type.play || [])) {
      if (play.play_name === playName) return play
    }
  }
  return false
}

// ---------------------------------------------------------------------------
// 接口
// ---------------------------------------------------------------------------

/**
 * 旧版 getGameCache(id)：先读 sessionStorage["game_info_"+id]，没有就 GET /game/lists
 * 找到该 game，再 POST /game/state 把 game_state 填进去，然后写缓存。
 */
async function getGameCache(id) {
  try {
    const cached = sessionStorage.getItem(`game_info_${id}`)
    if (cached) return JSON.parse(cached)
  } catch (_) { /* 缓存损坏时走接口 */ }
  const response = await request('/game/lists')
  const found = (response.data?.lists || []).find((item) => String(item.game_id) === String(id))
  if (!found) return null
  try {
    const state = await request('/game/state', { method: 'POST', data: { game: found.game_id } })
    found.game_state = state.data || {}
  } catch (_) { /* 状态拿不到时先用列表数据渲染 */ }
  try { sessionStorage.setItem(`game_info_${id}`, JSON.stringify(found)) } catch (_) { /* 存储不可用 */ }
  return found
}

/** 旧版 getLastLottery()：不带 game_no，取最近一期，只用来填 header 的号码球 */
async function getLastLottery() {
  const response = await request('/game/history', { method: 'POST', data: { game: gameId.value } })
  const rows = Array.isArray(response.data) ? response.data : (response.data?.lists || [])
  if (!rows.length) return
  lotteryGameNo.value = rows[0].game_no
  // 旧版直接 split(',')，不补零 —— 后端返回的就已经是两位格式
  lotteryNumbers.value = String(rows[0].history_lottery_numbers || '').split(',')
}

/**
 * 旧版 getState()：
 *   gameCloseState = lottery_time<0 && open_time<0 && close_time<0
 *   清掉旧的 1 秒计时器，替换 game_state，
 *   期号变化时补拉 getStatistic() + getTodayBill()（注意：不调 clearData），
 *   最后重新起 setInterval(setState, 1000)。
 */
async function getState() {
  const response = await request('/game/state', { method: 'POST', data: { game: gameId.value } })
  const data = response.data || {}
  gameCloseState.value = Number(data.lottery_time) < 0 && Number(data.open_time) < 0 && Number(data.close_time) < 0
  if (stateTimer) window.clearInterval(stateTimer)
  const previousGameNo = gameState.value.game_no
  gameState.value = { ...data }
  game.value.game_state = gameState.value
  try { sessionStorage.setItem(`game_info_${gameId.value}`, JSON.stringify(game.value)) } catch (_) { /* 存储不可用 */ }
  if (previousGameNo != data.game_no) {
    void getStatistic()
    void getTodayBill()
  }
  stateTimer = window.setInterval(setState, 1000)
}

/** 旧版 getOdds()：playList 存下来，同时把每个 play 按 play_name 挂到对应门上 */
async function getOdds() {
  const response = await request('/game/odds', { method: 'POST', data: { game: gameId.value } })
  playList.value = response.data || []
  for (const type of playList.value) {
    for (const play of (type.play || [])) {
      const key = String(play.play_name)
      if (Object.prototype.hasOwnProperty.call(betItems.value, key)) {
        betItems.value[key].play = play
      }
    }
  }
}

/** 旧版 getUserMoney() → GET /user/money */
async function getUserMoney() {
  const response = await request('/user/money')
  user.value = response.data || null
}

/** 旧版 getTodayBill() → POST /game/bet/today {game: game_id} */
async function getTodayBill() {
  const response = await request('/game/bet/today', { method: 'POST', data: { game: gameId.value } })
  if (response.data) todayBill.value = response.data
}

let statisticVersion = 0
let statisticTimer

// 消息只触发服务端汇总刷新，不能再把可能已包含在汇总中的注单累加一次。
// 同一批推送合并请求；收到新事件即使旧请求尚未返回，也使其失效。
function scheduleStatistic() {
  if (pageLeft) return
  statisticVersion += 1
  if (statisticTimer) return
  statisticTimer = window.setTimeout(() => {
    statisticTimer = undefined
    void getStatistic().catch(() => { /* 保留上次服务端结果，后续事件重新校准 */ })
  }, 100)
}

/** 总额和个人金额只接受服务端统计，过期请求不得写回当前盘面。 */
async function getStatistic() {
  if (pageLeft || !gameState.value.game_no) return
  const version = ++statisticVersion
  const game = gameId.value
  const period = gameState.value.game_no
  const response = await request('/game/statistic', {
    method: 'POST',
    data: { game, game_no: period, member: true, page: 1, pagesize: 15 },
  })
  if (pageLeft || version !== statisticVersion || game != gameId.value || period != gameState.value.game_no) return
  const data = response.data || {}
  if (!Array.isArray(data.total) || !Array.isArray(data.member)) return
  for (const key of gateOrder) {
    // play_name 可在赔率接口尚未完成时匹配门位。
    const matches = (row) => row.play_name === key || (betItems.value[key].play.play_id && betItems.value[key].play.play_id == row.play_id)
    const total = data.total.find(matches)
    const member = data.member.find(matches)
    betItems.value[key].bet.total = Number(total?.bet_money) || 0
    betItems.value[key].bet.member = Number(member?.bet_money) || 0
    betItems.value[key].bet.member_win = Number(member?.bet_money_win) || 0
  }
}

/** 旧版 getServiceWechatPic() → POST /config/detail {name:'service_wechat_pic'} */
async function getServiceWechatPic() {
  try {
    const response = await request('/config/detail', { method: 'POST', data: { name: 'service_wechat_pic' } })
    serviceWechatPic.value = typeof response.data === 'string' ? response.data : ''
  } catch (_) { /* 未配置客服图时按钮点开是空的，与旧版一致 */ }
}

// ---------------------------------------------------------------------------
// 开奖三段：setState 触发 → setLottery 写盘面 → lotteryAnimation 数 5 秒后清盘
// ---------------------------------------------------------------------------

/**
 * 旧版 setState()，每秒执行一次：
 *   close_time <= 0 → 所有门 close = true，否则全部 false
 *   三个时间各自 -1
 *   lottery_time <= -1 * game_lottery_time 时去拉本期开奖
 * 注意 leave 守卫，离开页面后不再执行。
 */
let guardTicks = 0
let guardRefreshing = false
function setState() {
  if (pageLeft) return
  const state = gameState.value
  if (!state) return
  if (++guardTicks % 10 === 0 && state.draw_guard?.enabled && !guardRefreshing) {
    guardRefreshing = true
    void getState().catch(() => {}).finally(() => { guardRefreshing = false })
  }
  const closed = Number(state.close_time) <= 0
  if (closed && gateOrder.some((key) => !betItems.value[key].close)) scheduleStatistic()
  for (const key of gateOrder) betItems.value[key].close = closed
  state.close_time = Number(state.close_time) - 1
  state.lottery_time = Number(state.lottery_time) - 1
  state.open_time = Number(state.open_time) - 1
  if (state.lottery_time <= -1 * Number(game.value.game_lottery_time || 0)) {
    void fetchCurrentLottery()
  }
}

/** 旧版 setState 里那次 getLotteryList({game, game_no}) 的回调 */
async function fetchCurrentLottery() {
  try {
    const response = await request('/game/history', {
      method: 'POST',
      data: { game: gameId.value, game_no: gameState.value.game_no },
    })
    // 带 game_no 时后端返回单条，不带时返回数组，这里两种都兼容
    const row = Array.isArray(response.data) ? response.data[0] : response.data
    setLottery(row)
    // 旧版无条件继续调这两个，即使 data 为空
    await getState()
    void getUserMoney()
  } catch (_) { /* 拉不到就等下一秒再试，与旧版一致 */ }
}

/**
 * 旧版 setLottery(l)：
 *   动画秒数复位 5、清掉旧动画计时器、lottery = true（遮罩立刻撤掉、牌面露出）
 *   lotteryGameNo / lotteryNumbers 换成本期
 *   format_lottery_numbers.result 按下标映射到 a,b,c,d,e
 *   game_state.lottery_time = 0（避免 setState 再次触发拉取）
 *   起 1 秒的 lotteryAnimation 计时器，放开奖音效
 */
function setLottery(data) {
  if (!data) return
  resetPendingBets()
  lotteryAnimationTime.value = 5
  if (lotteryAnimTimer) window.clearInterval(lotteryAnimTimer)
  lottery.value = true
  gateDetail.value = null
  lotteryGameNo.value = data.game_no
  lotteryNumbers.value = Object.prototype.hasOwnProperty.call(data, 'lottery_numbers')
    ? String(data.lottery_numbers || '').split(',')
    : String(data.history_lottery_numbers || '').split(',')
  const result = data.format_lottery_numbers?.result || []
  result.forEach((row, index) => {
    const key = gateOrder[index]
    if (!key) return
    betItems.value[key].lottery = {
      item: row.item || [0, 0],
      sum: row.sum,
      rank: row.rank,
    }
  })
  gameState.value.lottery_time = 0
  lotteryAnimTimer = window.setInterval(lotteryAnimation, 1000)
  playAudio('lottery')
}

/**
 * 旧版 lotteryAnimation()，每秒执行：
 *   lottery_animation_time <= 0 时复位成 5、停掉自己、弹上一期报表，
 *   然后 getState() + clearData() + getUserMoney() + getTodayBill()
 *   之后再 -1。
 * 从 5 开始每秒递减，第 6 次 tick 才进清盘分支，所以牌面停留约 6 秒。
 */
async function lotteryAnimation() {
  if (lotteryAnimationTime.value <= 0) {
    lotteryAnimationTime.value = 5
    if (lotteryAnimTimer) window.clearInterval(lotteryAnimTimer)
    await openStatisticModal(true)
    void getState()
    clearData()
    void getUserMoney()
    void getTodayBill()
  }
  lotteryAnimationTime.value -= 1
}

/**
 * 旧版 clearData()：每门的下注金额、落下的筹码、牌面全部归零，lottery 关掉。
 * 旧版把 bet.lists 设成 [v]（类本身，money 为 undefined），再靠模板的
 * `ngIf item.money > 0` 挡住，效果等于清空，这里直接清成空数组。
 */
function clearData() {
  statisticVersion += 1
  window.clearTimeout(statisticTimer)
  statisticTimer = undefined
  resetPendingBets()
  for (const key of gateOrder) {
    betItems.value[key].bet.total = 0
    betItems.value[key].bet.member = 0
    betItems.value[key].bet.member_win = 0
    betItems.value[key].bet.lists = []
    betItems.value[key].lottery = { item: [0, 0], sum: 0, rank: 0 }
  }
  lottery.value = false
  lotteryAnimationTime.value = 0
}

// ---------------------------------------------------------------------------
// 下注
// ---------------------------------------------------------------------------

/** 选择门位；筹码只有在选择门位后才会加入待确认下注。 */
function selectGate(playName, event) {
  if (Number(gameState.value.close_time) <= 0) return showToast('已封盘')
  selectedGate.value = playName
  customBetAmount.value = String(pendingBets.value[playName].find((chip) => chip.custom)?.money || '')
  const rect = event.currentTarget.getBoundingClientRect()
  selectedGatePoint.value = { x: rect.left + rect.width / 2 - 15, y: rect.top + rect.height / 2 - 15 }
}

/** 点击筹码只加入待确认队列，不会立即向服务端下注。 */
function changBetMoney(value, options = {}) {
  if (!selectedGate.value) return showToast('请先选择下注区域')
  if (Number(gameState.value.close_time) <= 0) return showToast('已封盘')
  betMoney.value = value
  const chips = pendingBets.value[selectedGate.value]
  if (options.custom) {
    const customChip = chips.find((chip) => chip.custom)
    if (customChip) {
      customChip.money = value
      return
    }
  }
  const offset = Math.min(chips.length * 5, 30)
  const point = selectedGatePoint.value || { x: window.innerWidth / 2 - 15, y: window.innerHeight / 2 - 15 }
  chips.push({ money: value, x: `${point.x + offset}px`, y: `${point.y + offset}px`, custom: Boolean(options.custom) })
  if (!options.custom) customBetAmount.value = String(value)
}

/** 输入金额立即加入（或更新）当前选中门位，无需额外按回车。 */
function syncCustomBet() {
  if (!selectedGate.value) return
  const chips = pendingBets.value[selectedGate.value]
  const value = Number(customBetAmount.value)
  if (!Number.isFinite(value) || value <= 0) {
    const index = chips.findIndex((chip) => chip.custom)
    if (index >= 0) chips.splice(index, 1)
    return
  }
  changBetMoney(value, { custom: true })
}

/** 使用可用余额中尚未加入待确认注单的全部金额。 */
function betAllIn() {
  const value = Number(user.value?.balance || 0) - Number(pendingBetTotal.value || 0)
  if (value <= 0) return showToast('可用余额不足')
  changBetMoney(value, { custom: true })
  customBetAmount.value = String(value)
}

/** 取消当前选中门位的待确认筹码。 */
function cancelPendingBet() {
  if (!selectedGate.value) return
  pendingBets.value[selectedGate.value] = []
  selectedGate.value = null
  selectedGatePoint.value = null
  betMoney.value = 0
  customBetAmount.value = ''
}

/** 清空本次尚未确认的全部下注。 */
function clearPendingBets() { resetPendingBets() }

/** 逐枚提交待确认筹码，保证筹码金额及台面图标与实际注单一致。 */
async function confirmPendingBets() {
  if (!pendingBetTotal.value) return showToast('请先选择区域和筹码')
  if (Number(gameState.value.close_time) <= 0) return showToast('已封盘')
  isConfirmingBets.value = true
  let succeeded = false
  try {
    for (const playName of gateOrder) {
      const play = getPlay(playName)
      if (!play) throw new Error('下注内容有误')
      while (pendingBets.value[playName].length) {
        const chip = pendingBets.value[playName][0]
        await request('/game/bet/bet', {
          method: 'POST',
          data: { game: gameId.value, game_no: gameState.value.game_no, play: play.play_id, money: chip.money, content: play.bet_content },
        })
        pendingBets.value[playName].shift()
        betItems.value[playName].bet.lists.push(chip)
        succeeded = true
      }
    }
    if (succeeded) {
      void getUserMoney()
      playAudio('bet')
      showToast('下注成功')
    }
    selectedGate.value = null
    selectedGatePoint.value = null
    betMoney.value = 0
    customBetAmount.value = ''
  } catch (error) {
    showToast(error.message || '下注失败，请重新确认剩余筹码')
  } finally {
    if (succeeded) scheduleStatistic()
    isConfirmingBets.value = false
  }
}

// ---------------------------------------------------------------------------
// 弹窗 / 菜单
// ---------------------------------------------------------------------------

/**
 * 旧版 statisticModal(l)：l=true 传 game_no=-1（后端解析成最近一期已开奖，
 * 也就是「上一期报表」），l=false 传当期期号。弹出后 showMenu = false。
 */
async function openStatisticModal(last) {
  statisticModal.value = { gameNo: last ? -1 : gameState.value.game_no }
  showMenu.value = { ...showMenu.value, show: false }
}

/** 旧版 ruleModal / billModal 是 Ionic modal，这里用页内全屏弹层复刻；
 *  betModal / historyModal / moneyModal 暂时还是跳对应路由页。 */
function openRule() {
  showRule.value = true
  showMenu.value = { ...showMenu.value, show: false }
}
/** 旧版 billModal()：modalCtrl.create({component: app-bill, componentProps:{game}}) */
function goBill() { showMenu.value = { ...showMenu.value, show: false }; showBill.value = true }
/** 旧版 betModal()：modalCtrl.create({component: app-bet, componentProps:{game}}) */
function goBets() { showMenu.value = { ...showMenu.value, show: false }; showBetList.value = true }
/** 旧版 historyModal()：modalCtrl.create({component: app-lottery, componentProps:{game}}) */
function goHistory() { showMenu.value = { ...showMenu.value, show: false }; showHistory.value = true }
/** 旧版 moneyModal(l=1)：modalCtrl.create({component: app-money, componentProps:{game, type:l, user}}) */
function goMoney(type) { showMenu.value = { ...showMenu.value, show: false }; moneyModal.value = { type } }
/** 新增：客服二维码。弹层还是旧版那一个，只是入口从顶栏笑脸挪到了右侧菜单 */
function openService() { showMenu.value = { ...showMenu.value, show: false }; showService.value = true }
/** 关掉充值/提现弹窗后顺手刷一次余额（申请可能已被即时处理） */
function closeMoneyModal() {
  moneyModal.value = null
  void getUserMoney().catch(() => {})
}

// ---------------------------------------------------------------------------
// 聊天室：以下函数逐条照搬大吃小 PjxBetPage.vue（旧版 chunk 29 的 app-pjx），
// 只有 chatCollectGates 按牌九的五门两张牌重写。
// ---------------------------------------------------------------------------

/**
 * 旧版 formatTime(n)：n <= 0 返回 "00:00"；不足一小时输出 mm:ss，
 * 满一小时则前置小时段输出 hh:mm:ss。各段不足两位补零。
 */
function formatTime(value) {
  const seconds = Number(value || 0)
  if (seconds <= 0) return '00:00'
  const pad = (input) => (input < 10 ? `0${input}` : String(input))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds / 60) % 60)
  const rest = Math.floor(seconds % 60)
  return `${hours > 0 ? `${pad(hours)}:` : ''}${pad(minutes)}:${pad(rest)}`
}

/** 把 format_lottery_numbers.source 解析成 1~10 的十个开奖号，同大吃小的 numbers() */
function numbers(value) {
  if (Array.isArray(value)) return value.map(Number).filter((item) => item >= 1 && item <= 10).slice(0, 10)
  return (String(value || '').match(/\d{1,2}/g) || []).map(Number).filter((item) => item >= 1 && item <= 10).slice(0, 10)
}

/** 与本文件下方的 wsData 等价，保留大吃小的命名以便逐条对照 */
function chatData(message) { return message?.data || {} }

function chatTimestamp(message) {
  const value = Number(message?.time || message?.timestamp || 0)
  return value > 1e12 ? Math.floor(value / 1000) : value
}

function chatTime(value) {
  const stamp = chatTimestamp({ time: value })
  if (!stamp) return ''
  const date = new Date(stamp * 1000)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * 气泡昵称行的发送时间，精确到秒（HH:mm:ss）。
 * 同一分钟内连下几注时，只有 HH:mm 会显示成一模一样，带上秒才分得清先后。
 * 居中的时间分隔仍旧走 chatTime() 的 HH:mm，保持旧版格式不变。
 */
function chatTimeFull(value) {
  const stamp = chatTimestamp({ time: value })
  if (!stamp) return ''
  const date = new Date(stamp * 1000)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
}

async function openGateDetail(key) {
  if (lottery.value) return
  const play = betItems.value[key]?.play
  if (!play?.play_id) return showToast('门位信息加载中')
  gateDetail.value = { key, total_money: 0, rows: [] }
  gateDetailLoading.value = true
  gateDetailError.value = ''
  try {
    const response = await request('/game/bet/gate_detail', {
      method: 'POST',
      data: { game: gameId.value, game_no: gameState.value.game_no, play: play.play_id },
    })
    const data = response.data || {}
    gateDetail.value = { key, total_money: data.total_money || 0, rows: Array.isArray(data.rows) ? data.rows : [] }
  } catch (error) {
    gateDetailError.value = error.message || '下注明细加载失败'
  } finally {
    gateDetailLoading.value = false
  }
}

function closeGateDetail() { gateDetail.value = null }
function openGateDetailChat() {
  closeGateDetail()
  selectChatBlock('chat')
}

function isOwnGateDetailRow(row) {
  return String(row.member_id ?? '') === String(user.value?.member_id ?? '')
    || String(row.member_username ?? '') === String(user.value?.username ?? '')
}

function canCancelGateDetailRow(row) {
  return isOwnGateDetailRow(row)
    && Number(gameState.value.close_time) > 0
    && !row.cancel
}

function requestGateDetailCancel(row) {
  if (canCancelGateDetailRow(row)) cancelConfirmTarget.value = { kind: 'gate-detail', row }
}

async function cancelGateDetailRow(row) {
  const betIds = Array.isArray(row.bet_ids) && row.bet_ids.length ? row.bet_ids : [row.bet_id]
  const results = await Promise.all(betIds.map((betId) => request('/game/bet/cancel', { method: 'POST', data: { bet_id: betId } })))
  if (!results.every((item) => Number(item.error_code || 0) === 0)) return
  row.cancel = true
  if (gateDetail.value) {
    gateDetail.value.rows = gateDetail.value.rows.filter((item) => item !== row)
    gateDetail.value.total_money = Math.max(0, Number(gateDetail.value.total_money || 0) - Number(row.bet_money || 0))
  }
  void getStatistic()
  void getUserMoney()
  void getTodayBill()
}

function chatName(message) {
  const data = chatData(message)
  return data.member_nickname || data.member?.member_nickname || data.member?.nickname || data.member_username || data.member?.member_username || data.member?.username || message?.member?.member_nickname || message?.member?.nickname || message?.member?.member_username || message?.member?.username || ''
}

/** 单注消息的 data 本身就是一条注单，批量注是 data.lists，统一成数组处理 */
function chatBets(message) {
  const data = chatData(message)
  return data.lists || data.bet_lists || (Array.isArray(data) ? data : [data])
}

/** 聊天气泡合并只使用稳定的会员标识；缺少标识时不合并，避免误并不同玩家。 */
function chatBetMemberKey(message) {
  const data = chatData(message)
  return String(data.member_id ?? data.member?.member_id ?? data.member_username ?? data.member?.member_username ?? '')
}
function chatBetGateKey(betRow) {
  return String(betRow?.play_name ?? betRow?.play_id ?? betRow?.bet_content ?? '')
}

/** 展示时按门汇总金额，原始注单仍保留给撤单、跟投和结算顺序使用。 */
function chatBetDisplayRows(message) {
  const groups = new Map()
  chatBets(message).filter((betRow) => !betRow.cancel).forEach((betRow) => {
    const key = chatBetGateKey(betRow)
    const current = groups.get(key)
    const money = Number(betRow.bet_money ?? betRow.money ?? 0)
    if (current) {
      current.bet_money += money
      current.money += money
      current.count += 1
      return
    }
    groups.set(key, { ...betRow, key, bet_money: money, money, count: 1 })
  })
  return [...groups.values()]
}

function isMyChatMessage(message) {
  const data = chatData(message)
  const own = [user.value?.username, user.value?.member_username, user.value?.nickname, user.value?.member_nickname].filter(Boolean).map(String)
  const sender = [data.member_username, data.member?.member_username, data.member?.username, data.member_nickname, data.member?.member_nickname, data.member?.nickname, message?.member?.member_username, message?.member?.username, message?.member?.member_nickname, message?.member?.nickname].filter(Boolean).map(String)
  return sender.some((name) => own.includes(name))
}

function chatGameNo(message) { const data = chatData(message); return data.game_no || data.game?.game_no || '' }

/** 头像相对路径挂在后端站点根上，复用本页已推导出的 assetOrigin */
function chatAvatar(message) {
  const data = chatData(message)
  const value = data.member_avatar || data.member?.member_avatar || data.member?.avatar || message?.member?.member_avatar || message?.member?.avatar || (isMyChatMessage(message) ? user.value?.avatar : '')
  if (!value || value === '/assets/userface.png') return '/legacy/images/userface-default.png'
  return /^(?:https?:|data:|blob:)/i.test(value) ? value : `${assetOrigin}${value.startsWith('/') ? value : `/${value}`}`
}

function onChatAvatarError(event) { event.target.onerror = null; event.target.src = '/legacy/images/userface-default.png' }

function chatStatisticName(item) { return item.member_nickname || item.member_username || item.member?.member_nickname || item.member?.nickname || item.member?.member_username || item.member?.username || '' }

/**
 * 注单行的门号文本。牌九后端的 play_title 是「一」「二」…（不带「门」字），
 * 直接拿来用会显示成「二投了：」，所以补上「门」与开奖卡片的「二门」对齐。
 * 已经带「门」时不重复追加，避免出现「二门门」。
 * play_title 缺失时回退到 gateLabel 的阿拉伯数字（1~5）。
 */
function chatBetGateLabel(betRow) {
  const title = String(betRow?.play_title || '').trim()
  const base = title || String(gateLabel[betRow?.play_name] ?? betRow?.play_name ?? '')
  if (!base) return ''
  if (isOzbPj.value && base.endsWith('号位')) return `${base.slice(0, -2)}门`
  return base.endsWith('门') ? base : `${base}门`
}
function chatStatisticPage(message) { const page = chatData(message).page || {}; return page.total ? `(${page.index || 1}/${page.total})` : '' }

function chatCloseCheckGates(message) {
  const source = chatData(message).gates || []
  return [1, 2, 3, 4, 5].map((gate) => source.find((item) => Number(item.gate) === gate) || { gate, total_money: 0, rows: [] })
}

/**
 * 开奖公告卡片：普通牌九按十个开奖号两两分成五门；澳洲幸运5直接使用五个位置号。
 *
 * 点数 = 两球相加取个位，已用实际开奖逐门验证过：
 *   4+7=11→1点、10+5=15→5点、6+8=14→4点、1+3=4→4点、9+2=11→1点
 *
 * 不做名次：名次要看牌型（对子/天九王等），前端只有两个点数算不出来，
 * 所以这里刻意只呈现点数，与牌桌上那张「N点」保持一致。
 */
function chatCollectGates(message) {
  const formatted = chatData(message).format_lottery_numbers || {}
  if (isOzbPj.value) {
    const result = Array.isArray(formatted.result) ? formatted.result : []
    return gateCnLabel.map((label, index) => {
      const point = Number(result[index]?.sum)
      return {
        gate: index + 1,
        label,
        balls: [],
        point: Number.isInteger(point) && point >= 0 && point <= 9 ? point : null,
        position: true,
      }
    })
  }
  const source = numbers(formatted.source)
  return gateCnLabel.map((label, index) => {
    const first = source[index * 2]
    const second = source[index * 2 + 1]
    const hasBoth = Number.isInteger(first) && Number.isInteger(second)
    return {
      gate: index + 1,
      label,
      // 号码球在这里就补好两位文本，模板里只负责取色和渲染，
      // 不在模板表达式里调 Number.isInteger / String.padStart。
      balls: [first, second].map((ball) => ({
        value: ball,
        text: Number.isInteger(ball) ? String(ball).padStart(2, '0') : '',
      })),
      // 个位数即点数；两球缺一不全时不显示点数，避免算出误导性的 0 点
      point: hasBoth ? (first + second) % 10 : null,
      position: false,
    }
  })
}

/** 旧版四个筛选按钮对应的可见性判断 */
function chatMessageVisible(message) {
  if (message.type === 'collect') return chatShow.value.collect
  if (message.type === 'statistic') return chatShow.value.statistic
  if (message.type === 'close_check') return chatShow.value.close_check
  if (message.type === 'bet' || message.type === 'bet_batch') return isMyChatMessage(message) ? chatShow.value.bet_me : chatShow.value.bet_other
  return true
}

function toggleChatFilter(key) { chatShow.value = { ...chatShow.value, [key]: !chatShow.value[key] } }

async function scrollChatContent() {
  await nextTick()
  if (chatScroll.value) chatScroll.value.scrollTop = chatScroll.value.scrollHeight
}

/**
 * 居中时间分隔的间隔阈值（秒）。旧版是 60，这里放大到 300 ——
 * 每个气泡的昵称行已经带了发送时间（方案 A），60 秒一条居中分隔会显得重复。
 * 想完全回到旧版节奏，把这里改回 60 即可，其余代码不用动。
 */
const CHAT_TIME_DIVIDER_GAP = 300

/**
 * 旧版 scrollChatContent(msg)：超过 100 条先砍掉最早一条，
 * 与上一条间隔超过阈值时插一条居中时间分隔，然后落条并滚到底。
 */
function appendChatMessage(message) {
  if (!message?.type) return
  // 全部牌九：同一玩家、同一期、同一秒、同一门的单注只合并聊天显示。
  // 原始注单保持独立，避免影响结算顺序、撤单及跟投。
  if (message.type === 'bet' && chatBets(message).length === 1) {
    const stamp = chatTimestamp(message)
    const incomingBet = chatBets(message)[0]
    const memberKey = chatBetMemberKey(message)
    const gateKey = chatBetGateKey(incomingBet)
    const target = memberKey && gateKey && [...chatMessages.value].reverse().find((item) => {
      if (item.type !== 'bet' || chatTimestamp(item) !== stamp) return false
      if (chatBetMemberKey(item) !== memberKey || String(chatGameNo(item)) !== String(chatGameNo(message))) return false
      const rows = chatBets(item)
      return rows.length > 0 && rows.every((row) => chatBetGateKey(row) === gateKey)
    })
    if (target) {
      const targetData = chatData(target)
      // 首笔单注本身就是 target.data，不能把它直接塞回 target.data.lists，
      // 否则会产生自引用对象；这里保留一份普通注单副本。
      const existingRows = targetData.lists || targetData.bet_lists || [{ ...targetData }]
      targetData.lists = [...existingRows, incomingBet]
      void scrollChatContent()
      return
    }
  }
  if (chatMessages.value.length >= 100) chatMessages.value.splice(0, 1)
  const stamp = chatTimestamp(message)
  if (stamp && stamp - lastChatTimestamp >= CHAT_TIME_DIVIDER_GAP) chatMessages.value.push({ type: 'time', time: stamp, id: `time-${stamp}` })
  if (stamp) lastChatTimestamp = stamp
  chatMessages.value.push({ ...message, id: message.id || `${message.type}-${stamp || Date.now()}-${Math.random()}` })
  void scrollChatContent()
}

/** 跟投/撤单都要求是当期且未封盘 */
function canFollowChatBet(message) {
  return String(chatGameNo(message)) === String(gameState.value.game_no || '') && Number(gameState.value.close_time) > 0
}

// 聊天室跟投不生成牌桌筹码坐标；确认后仍使用牌九原有单注接口。
const { target: followTarget, busy: followBusy, error: followError, toast: followToast, validation: followValidation, open: openFollow, close: closeFollow, confirm: confirmFollow } = useChatFollowBet({
  balance: availableBalance,
  single: true,
  allowed: (target) => target.game === gameId.value && canFollowChatBet(target.message) && !chatData(target.message).cancel,
  send: async ([entry], target) => {
    const money = Number(entry.amount)
    await request('/game/bet/bet', {
      method: 'POST',
      data: {
        game: target.game,
        game_no: target.period,
        play: entry.playId,
        money,
        content: entry.content,
      },
    })
    scheduleStatistic()
    playAudio('bet')
  },
  afterSuccess: () => Promise.all([getUserMoney(), getTodayBill()]),
})

function openChatFollow(message, bets) {
  if (!canFollowChatBet(message)) return
  openFollow({ message, player: chatName(message), period: chatGameNo(message), game: gameId.value,
    entries: bets.map((bet) => {
      const playName = String(bet?.play_name || '')
      const play = getPlay(playName)
      return { playId: betItems.value[playName] ? play?.play_id : null, playName, content: play?.bet_content, label: bet.play_title || bet.play_name, amount: bet.bet_money ?? bet.money ?? 0 }
    }),
  })
}

function followChatBet(message, betRow) {
  openChatFollow(message, [betRow])
}

function followChatBetBatch(message) {
  openChatFollow(message, chatBets(message))
}

function isChatBetCancelable(message) {
  return isMyChatMessage(message)
    && String(chatGameNo(message)) === String(gameState.value.game_no || '')
    && Number(gameState.value.close_time) > 0
    && !chatData(message).cancel
}

function requestChatCancel(message) { if (isChatBetCancelable(message)) cancelConfirmTarget.value = { kind: 'chat', message } }

/** 旧版撤单：逐条 POST /game/bet/cancel {bet_id}，全部成功才把消息标记成已取消 */
async function confirmChatCancel() {
  const target = cancelConfirmTarget.value
  cancelConfirmTarget.value = null
  if (target?.kind === 'gate-detail') {
    try { await cancelGateDetailRow(target.row) } catch (_) { /* 保留明细，等待用户重试 */ }
    return
  }
  const message = target?.message
  if (!message) return
  try {
    const bets = chatBets(message)
    const results = await Promise.all(bets.map((betRow) => request('/game/bet/cancel', { method: 'POST', data: { bet_id: betRow.bet_id } })))
    if (results.every((item) => Number(item.error_code || 0) === 0)) {
      chatData(message).cancel = true
      bets.forEach((betRow) => { betRow.cancel = true })
      void getStatistic()
      void getUserMoney()
      void getTodayBill()
    }
  } catch (_) { /* 撤单被拒时保留原消息，与旧版一致 */ }
}

/** 旧版 selectChatBlock(type)：切到聊天室时清未读并滚到底 */
function selectChatBlock(type) {
  playType.value = type
  // 菜单是绝对定位在牌桌上的，切 tab 时顺手收起，避免残留在聊天室上层
  showMenu.value = { ...showMenu.value, show: false }
  if (type === 'chat') {
    chatNewCount.value = 0
    void scrollChatContent()
  }
}

/**
 * 右上角笑脸按钮：原本是切客服图弹层，现在改成开/关聊天室（客服入口已挪到右侧菜单）。
 * 保持旧版 showService = !showService 的「切换」语义而不是只开不关 ——
 * 顶栏在聊天室里是常驻的，若只负责打开，进了聊天室再点它就毫无反应。
 */
function openChat() { selectChatBlock(playType.value === 'chat' ? 'play' : 'chat') }

/** 旧版发送：{type:'chat', game, message}，成功后清空输入框 */
function sendChatMessage() {
  const message = chatContent.value.trim()
  if (!message || !socket || socket.readyState !== WebSocket.OPEN) return
  socket.send(JSON.stringify({ type: 'chat', game: gameId.value, message }))
  chatContent.value = ''
}

// ---------------------------------------------------------------------------
// WebSocket，逐条对应旧版 _wsOpen / _wsMessage / _wsClose
// ---------------------------------------------------------------------------

function wsData(event) { return event?.data || {} }

function connectWsServer(reconnect = false) {
  const token = authStorage.getAccessToken()
  if (!token || !apiConfig.wsServer || pageLeft || (socket && socket.readyState <= 1)) return
  try { socket = new WebSocket(apiConfig.wsServer) } catch (_) { socketRecovery.retry(); return }
  const activeSocket = socket
  socket.binaryType = 'arraybuffer'
  socket.onopen = () => {
    if (socket !== activeSocket || pageLeft) return
    if (reconnect) enteredAt = Math.floor(Date.now() / 1000)
    reConnectNumber = 0
    showReconnectAlert.value = false
    if (!reconnect) scheduleStatistic()
    // 旧版 _wsOpen：!reconnect 时先清空聊天记录（重连则带 reconnect:1，
    // 让服务端从 GameMsg 补推历史，本地记录要保留着接上去）
    if (!reconnect) {
      chatMessages.value = []
      lastChatTimestamp = 0
    }
    // 旧版 _wsOpen：{type:'connect', game, client:'web-user', token}，重连补 reconnect:1
    const payload = { type: 'connect', game: gameId.value, client: 'web-user', token }
    if (reconnect) payload.reconnect = 1
    try { socket?.send(JSON.stringify(payload)) } catch (_) { /* 连接已断 */ }
    socketRecovery.opened(reconnect)
  }
  socket.onmessage = (message) => {
    try { handleWsMessage(message) } catch (_) { /* 保留当前盘面 */ }
  }
  socket.onclose = (event) => {
    if (socket !== activeSocket) return
    socket = undefined
    reConnectNumber += 1
    socketRecovery.retry(event)
  }
  socket.onerror = () => { try { activeSocket.close() } catch (_) {} }
}

function handleWsMessage(message) {
  const envelope = JSON.parse(message.data)
  if (envelope.error_code != 0) return
  const event = envelope.data
  const type = event?.type
  const time = parseInt(event?.time, 10)
  switch (type) {
    // 旧版 collect：期号不是当期就整盘清空，是当期才写开奖
    case 'collect': {
      appendChatMessage(event)
      if (time > enteredAt) {
        if (wsData(event).game_no != gameState.value.game_no) { clearData(); break }
        setLottery(wsData(event))
      }
      // 新增：开奖公告进聊天流。与大吃小一致，聊天流无条件收（包含重连时
      // 服务端补推的历史消息），只有写盘面那一步才受 time > enteredAt 约束。
      break
    }
    // 下注推送只刷新统计，历史补发或重复投递都不能重复计入金额。
    case 'bet': {
      const data = wsData(event)
      if (data.game_id != gameId.value) break
      // 新增：单注进聊天流。
      // 这里刻意不照搬大吃小 —— 旧版大吃小的 bet 分支只累加 chatNewCount、
      // 不调 scrollChatContent（见 chunk 29 的 case"bet"），因为大吃小下注走
      // /game/bet/bet_batch，聊天流靠 bet_batch 承载。牌九全部注单都是单注
      // （/game/bet/bet → Game.php:284 推 type 'bet'），照搬会让「我的投注 /
      // 他人投注」两个筛选永远为空，所以改用番摊的做法（FantanBetPage.vue
      // 的 chatMessageVisible 同样把 bet 计入流）。
      appendChatMessage(event)
      if (playType.value !== 'chat') chatNewCount.value += 1
      if (data.game_no == gameState.value.game_no && betItems.value[data.play_name]) {
        scheduleStatistic()
      }
      // 自己提交成功时已播放音效。
      if (data.member_username === user.value?.username) break
      if (time > enteredAt) playAudio('bet')
      break
    }
    // 新增：批量注。牌九自己不会产生（没有批量下注入口），保留兜底以防
    // 同一房间里有别的客户端用 /game/bet/bet_batch 下注（Game.php:346）。
    case 'bet_batch': {
      const data = wsData(event)
      if (data.game?.game_id != gameId.value) break
      appendChatMessage(event)
      if (playType.value !== 'chat') chatNewCount.value += 1
      if ((data.lists || []).some((row) => row.game_no == gameState.value.game_no && betItems.value[row.play_name])) scheduleStatistic()
      if (data.member?.member_username === user.value?.username) break
      if (time > enteredAt) playAudio('bet')
      break
    }
    // 旧版 bet_cancel：先回滚总额，是自己的注单再回滚个人额
    case 'bet_cancel': {
      const data = wsData(event)
      // 新增：先无条件把聊天流里对应的注单标成已取消。旧版大吃小同样是先标记
      // 再判断实时性（chunk 29 的 case"bet_cancel" 里那个 for 循环在
      // o<=inTimestamp 的 break 之前），所以历史消息也会被正确标记。
      chatMessages.value.forEach((item) => {
        if (item.type !== 'bet' && item.type !== 'bet_batch') return
        chatBets(item).forEach((betRow) => {
          if (String(betRow.bet_id) === String(data.bet_id)) {
            betRow.cancel = true
            chatData(item).cancel = chatBets(item).every((row) => row.cancel)
          }
        })
      })
      if (data.game_id != gameId.value || data.game_no != gameState.value.game_no) break
      if (!betItems.value[data.play_name]) break
      // 撤单推送可能与本地的统计刷新交错或重复到达；直接减法会让同一笔钱被扣两次。
      // 以统计接口的服务端汇总为唯一结果，确保桌面总投注与「详细」明细一致。
      void getStatistic()
      break
    }
    // 新增：用户聊天。后端默认分支推的 type 就是 'chat'（Game.php 的 message
    // 分支，WS 下发字段为 chat），与大吃小完全同构。
    case 'chat': {
      appendChatMessage(event)
      if (playType.value !== 'chat') chatNewCount.value += 1
      break
    }
    // 新增：开奖报表（Game.php:222）。旧版大吃小在这里顺手刷余额和今日账单。
    case 'statistic': {
      appendChatMessage(event)
      if (playType.value !== 'chat') chatNewCount.value += 1
      void getUserMoney()
      void getTodayBill()
      break
    }
    case 'close_check': {
      appendChatMessage(event)
      if (wsData(event).game_no == gameState.value.game_no) scheduleStatistic()
      if (playType.value !== 'chat') chatNewCount.value += 1
      break
    }
    // 旧版 recharge / cash 分支是空的（只有 *_handle 才提示）
    case 'recharge':
    case 'cash':
      break
    case 'connect':
    case 'online':
    case 'close':
      online.value = Number(wsData(event).count || 0)
      break
    case 'recharge_handle': {
      const data = wsData(event)
      if (handledRechargeIds.indexOf(data.recharge_id) !== -1) break
      showToast('您的充值申请已处理')
      if (time > enteredAt) { void getUserMoney(); playAudio('recharge_handle') }
      handledRechargeIds.push(data.recharge_id)
      try { localStorage.setItem('chatRechargeIds', JSON.stringify(handledRechargeIds)) } catch (_) { /* 存储不可用 */ }
      break
    }
    case 'cash_handle': {
      const data = wsData(event)
      if (handledCashIds.indexOf(data.cash_id) !== -1) break
      showToast('您的提现申请已处理')
      if (time > enteredAt) void getUserMoney()
      playAudio('cash_handle')
      handledCashIds.push(data.cash_id)
      try { localStorage.setItem('chatCashIds', JSON.stringify(handledCashIds)) } catch (_) { /* 存储不可用 */ }
      break
    }
    case 'cash_cancel': {
      const data = wsData(event)
      if (handledCashIds.indexOf(data.cash_id) !== -1) break
      showToast('您有提现申请已补取消，请息知')
      if (time > enteredAt) void getUserMoney()
      playAudio('cash_cancel')
      handledCashIds.push(data.cash_id)
      try { localStorage.setItem('chatCashIds', JSON.stringify(handledCashIds)) } catch (_) { /* 存储不可用 */ }
      break
    }
    // 后端还会推 lottery（中奖通知，abstracts/Game.php:769）。旧版大吃小的
    // chatShow 里 lottery 这一项初始就是 false（默认不显示），界面上也没给它
    // 筛选按钮，所以这里和 PjxBetPage 一样不接进聊天流。
    default:
      break
  }
}

/** 旧版 _wsClose 的「重连」按钮：次数归零、重连、再 initGame */
async function reconnectSocket() {
  showReconnectAlert.value = false
  reConnectNumber = 0
  try { socket?.close(3001, 'exit') } catch (_) { /* 已断开 */ }
  socket = undefined
  connectWsServer(true)
}

// ---------------------------------------------------------------------------
// 生命周期
// ---------------------------------------------------------------------------

/**
 * 旧版 initGame(l)：
 *   inTimestamp = 秒级时间戳、leave = false、game = await getGameCache(l)
 *   然后并列发起 getLastLottery / getState / getOdds / getUserMoney /
 *   getStatistic / getTodayBill，最后读回音效开关。
 *
 * 首次 getStatistic 时 game_state.game_no 往往还是空（getState 是异步的），
 * 旧版就有这个竞态，真正生效的是 getState 回调里期号变化时补的那一次。
 */
async function initGame(id) {
  enteredAt = Math.floor(Date.now() / 1000)
  pageLeft = false
  const cached = await getGameCache(id)
  if (cached) {
    game.value = cached
    gameState.value = { ...(cached.game_state || {}) }
  }
  void getLastLottery().catch(() => { /* header 号码球拿不到就保留默认 */ })
  void getState().catch((error) => showToast(error.message || '加载失败'))
  void getOdds().catch(() => { /* 赔率拿不到时下注会提示「下注内容有误」 */ })
  void getUserMoney().catch(() => { /* 余额拿不到时显示 0 */ })
  void getStatistic().catch(() => { /* 统计拿不到时盘口显示 0 */ })
  void getTodayBill().catch(() => { /* 今日输赢拿不到时显示 0 */ })
  try {
    const saved = localStorage.getItem(`game_audio_${game.value.game_id || id}`)
    if (saved !== null) audioOn.value = saved === 'true'
  } catch (_) { /* 存储不可用 */ }
}

onMounted(async () => {
  // 旧版 ngOnInit
  void getServiceWechatPic()
  try {
    handledCashIds = JSON.parse(localStorage.getItem('chatCashIds') || '[]')
    handledRechargeIds = JSON.parse(localStorage.getItem('chatRechargeIds') || '[]')
  } catch (_) { handledCashIds = []; handledRechargeIds = [] }
  // 旧版 ionViewDidEnter
  await initGame(route.params.game)
  connectWsServer(false)
})

onUnmounted(() => {
  socketRecovery.dispose()
  // 旧版 ionViewDidLeave
  pageLeft = true
  statisticVersion += 1
  window.clearTimeout(statisticTimer)
  if (stateTimer) window.clearInterval(stateTimer)
  if (lotteryAnimTimer) window.clearInterval(lotteryAnimTimer)
  window.clearTimeout(socketRetryTimer)
  window.clearTimeout(toastTimer)
  try { socket?.close(3001, 'exit') } catch (_) { /* 已断开 */ }
  try { localStorage.setItem(`game_audio_${gameId.value}`, String(audioOn.value)) } catch (_) { /* 存储不可用 */ }
})
</script>

<template>
  <ChatFollowBetModal :target="followTarget" :balance="availableBalance" :busy="followBusy" :error="followError" :validation="followValidation" :toast="followToast" @close="closeFollow" @confirm="confirmFollow" />
  <!-- 旧版根节点是 div.content -->
  <main class="paijiu-page">
    <div class="content">
      <p v-if="gameState.draw_guard?.blocked" role="status" style="margin:0;padding:10px;background:#fff3cd;color:#8a4b08;text-align:center">{{ gameState.draw_guard.message }}</p>
      <div class="pk-header">
        <!-- 旧版 header-left 是 routerLink="/"（牌九是自绘顶栏，不是 ion-toolbar，
             所以跟其它下注页写死的 "/tabs/home" 形式不同）。
             旧版 "/" 会被路由表 {path:"",redirectTo:"/tabs/home"} 转到彩票大厅；
             新版 "/" → /tabs → /home，落点一致，别改成 /game。 -->
        <div class="header-left" @click="router.push('/')"><img src="/legacy/images/game/paijiu/layout/back.png" alt="返回" /></div>
        <div class="header-center">
          <div class="game-info">
            <span class="game-title"> {{ game.game_title }} </span> 第 <span class="game-game-no"> {{ lotteryGameNo }} </span> 期
          </div>
          <div class="game-lottery">
            <!-- 旧版 ngFor lotteryNumbers，图片路径直接拼号码，不补零 -->
            <template v-if="lotteryNumbers">
              <div v-for="(ball, index) in lotteryNumbers" :key="`${ball}-${index}`">
                <span v-if="isOzbPj" class="ozbpj-number">{{ ball }}</span>
                <img v-else :src="`/legacy/images/game/paijiu/ball/${ball}.png`" alt="" />
              </div>
            </template>
          </div>
        </div>
        <!--
          旧版 header-right 点击是切换客服图弹层。现在改成打开聊天室：
          客服二维码入口挪到右侧菜单（弹层代码原样保留在下面），按既定方案
          这里不叠未读红圈，未读数只显示在聊天室 tab 上。

          chatEnabled 对应后台游戏列表的「聊天室」开关，旧版依据是
          chunk 24/26/27/28/29/30 模板里的 *ngIf="game.game_chat"。
          关掉时用等宽空占位补上，避免 header-center 的 flex:1 把标题挤偏。
        -->
        <div v-if="chatEnabled" class="header-right" @click="openChat"><img src="/legacy/images/game/paijiu/layout/service.png" alt="聊天室" /></div>
        <div v-else class="header-right"></div>
      </div>

      <!--
        聊天室浮层：从 pk-header 下方开始占满剩余空间，顶栏常驻。
        作为 .content 的 flex 子元素与 bet-main / pk-footer 并列，切到聊天室时
        下注区走 v-show（display:none）保留、不卸载，所以牌桌上的筹码、盘口
        金额、开奖牌面在来回切 tab 后都还在。下注区自身的结构一行未改。
      -->
      <section v-if="chatEnabled" v-show="playType === 'chat'" class="paijiu-chat">
        <!--
          结构对齐旧版 .play-type：外层按钮负责占位和居中，内层 span 才是带
          下边框的元素，所以选中态红线宽度 = 文字宽 + span 左右各 20px padding。
          第一个 tab 的标题按既定方案是「牌九」，点它切回牌桌。
          倒计时条件同旧版：close_time >= 0 && playType == 'chat'。
        -->
        <nav class="chat-tabs">
          <button type="button" :class="{ active: playType === 'play' }" @click="selectChatBlock('play')">
            <span>五门<span v-if="showTabCountdown" class="time">({{ formatTime(gameState.close_time) }})</span></span>
          </button>
          <button type="button" :class="{ active: playType === 'chat' }" @click="selectChatBlock('chat')">
            <span>聊天室({{ online }})<b v-if="chatNewCount > 0 && chatNewCount < 100">{{ chatNewCount }}</b><b v-else-if="chatNewCount >= 100">99+</b></span>
          </button>
        </nav>

        <!-- 旧版 .account-strip 金额条 -->
        <section class="chat-account-strip">
          <span>可用金额：<b>{{ fmt2(availableBalance) }}</b></span>
          <span>未结金额：<em>{{ fmt2(unsettledAmount) }}</em></span>
          <span>今日输赢：<em>{{ fmt2(todayProfit) }}</em></span>
        </section>

        <div ref="chatScroll" class="chat-container">
          <article v-for="message in orderedChatMessages" v-show="chatMessageVisible(message)" :key="message.id" :class="['chat-msg-item', `chat-msg-${message.type}`]">
            <!-- 居中时间分隔：间隔超过 CHAT_TIME_DIVIDER_GAP 秒时插入，格式沿用旧版的 HH:mm -->
            <p v-if="message.type === 'time'" class="chat-msg-time">{{ chatTime(message.time) }}</p>

            <!-- 用户聊天 -->
            <template v-else-if="message.type === 'chat'">
              <div :class="['chat-message', isMyChatMessage(message) ? 'chat-me' : 'chat-other']">
                <img class="chat-avatar" :src="chatAvatar(message)" alt="" @error="onChatAvatarError">
                <div class="chat-msg-content">
                  <!-- 昵称行右侧补一个发送时间（方案 A）。昵称贴着头像那一侧，
                       时间在远离头像的一侧，左右两种气泡靠 flex-direction 对称。 -->
                  <p class="chat-nickname"><span>{{ chatName(message) }}</span><time>{{ chatTimeFull(message.time) }}</time></p>
                  <p class="chat-msg-text">{{ chatData(message).message || chatData(message).content }}</p>
                </div>
              </div>
            </template>

            <!-- 注单：牌九全部是单注，bet_batch 只是兜底 -->
            <template v-else-if="message.type === 'bet' || message.type === 'bet_batch'">
              <div :class="['chat-message', isMyChatMessage(message) ? 'chat-me' : 'chat-other']">
                <img class="chat-avatar" :src="chatAvatar(message)" alt="" @error="onChatAvatarError">
                <div class="chat-msg-content">
                  <!-- 昵称行右侧补一个发送时间（方案 A）。昵称贴着头像那一侧，
                       时间在远离头像的一侧，左右两种气泡靠 flex-direction 对称。 -->
                  <p class="chat-nickname"><span>{{ chatName(message) }}</span><time>{{ chatTimeFull(message.time) }}</time></p>
                  <div class="chat-bet-msg">
                    <p>{{ isMyChatMessage(message) ? '我刚刚投了' : '刚刚投了' }} <b>{{ chatGameNo(message) }}</b> 期 {{ chatBets(message).length }} 注</p>
                    <div class="chat-bet-detail">
                      <div class="chat-bet-lines">
                        <div v-for="betRow in chatBetDisplayRows(message)" :key="betRow.key" class="chat-bet-line">
                          <span>{{ chatBetGateLabel(betRow) }}投了：<b>{{ fmt2(betRow.bet_money || betRow.money) }}</b></span>
                        </div>
                      </div>
                      <div class="chat-bet-action">
                        <button v-if="isMyChatMessage(message)" type="button" :disabled="!isChatBetCancelable(message)" @click="requestChatCancel(message)">{{ chatData(message).cancel ? '已取消' : '取消' }}</button>
                        <button v-else type="button" :disabled="!canFollowChatBet(message) || chatData(message).cancel" @click="message.type === 'bet_batch' || chatBets(message).length > 1 ? followChatBetBatch(message) : followChatBet(message, chatBets(message)[0])">{{ chatData(message).cancel ? '已取消' : '跟投' }}</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!--
              开奖公告卡片（既定「方案 A」）：三行 —— 门号 / 两个彩色号码球 / 「N点」。
              牌九和大吃小共用同一份十个开奖号，这里按顺序两两配成五门，
              点数取两球之和的个位（见 chatCollectGates 的注释与验证数据）。
              不做名次：名次要看牌型，前端算不出来。
            -->
            <section v-else-if="message.type === 'collect'" class="chat-msg-card chat-collect-card">
              <strong>第 {{ chatData(message).game_no }} 期开奖公告</strong>
              <div class="chat-collect-gates">
                <div v-for="item in chatCollectGates(message)" :key="item.gate" :class="['collect-gate', { 'position-gate': item.position }]">
                  <span class="collect-gate-label">{{ item.label }}门</span>
                  <span v-if="!item.position" class="collect-gate-balls">
                    <i v-for="(ball, index) in item.balls" :key="index" :class="color(ball.value)">{{ ball.text }}</i>
                  </span>
                  <span v-if="item.point !== null" class="collect-gate-point">{{ item.point }}点</span>
                </div>
              </div>
            </section>

            <!-- 开奖报表卡片 -->
            <section v-else-if="message.type === 'statistic'" class="chat-msg-card chat-statistic-card">
              <!-- 期号取 chatGameNo：statistic 消息的期号在 data.game.game_no，
                   不在 data.game_no（见 common/logic/Game.php:222 的推送结构）。 -->
              <strong>第 {{ chatGameNo(message) }} 期开奖报表{{ chatStatisticPage(message) }}</strong>
              <div class="chat-stat-head"><b>昵称</b><b>下注</b><b>中奖</b><b>盈利</b></div>
              <div v-for="item in chatData(message).lists || []" :key="item.member_id || chatStatisticName(item)" class="chat-stat-row">
                <span>{{ chatStatisticName(item) }}</span>
                <span class="chat-stat-money">{{ fmt2(item.bet_money) }}</span>
                <span class="chat-stat-money">{{ fmt2(item.bet_money_win ?? item.win_money) }}</span>
                <span :class="Number(item.bet_money_pl ?? item.pl_money) < 0 ? 'chat-stat-loss' : 'chat-stat-money'">{{ fmt2(item.bet_money_pl ?? item.pl_money) }}</span>
              </div>
            </section>

            <section v-else-if="message.type === 'close_check'" class="chat-msg-card chat-close-check-card">
              <strong>第 {{ chatData(message).game_no }} 期</strong>
              <p class="close-check-total">核对列表【本期下注总金额：{{ fmt2(chatData(message).total_money) }}】</p>
              <div v-for="gate in chatCloseCheckGates(message)" :key="gate.gate" class="close-check-gate">
                <b>【{{ gate.gate }}门总投：{{ fmt2(gate.total_money) }}】</b>
                <p v-if="!gate.rows?.length" class="close-check-none">无</p>
                <p v-for="row in gate.rows || []" :key="`${row.member_id}-${row.bet_create_time}`" class="close-check-row"><span>{{ row.member_name }} - 投：{{ fmt2(row.bet_money) }}</span><BetDetailTime :row="row" :rows="gate.rows" :format-time="chatTimeFull" /></p>
              </div>
            </section>
          </article>
        </div>

        <footer class="chat-footer">
          <!-- 旧版四个筛选按钮，对应 chatShow 的 bet_me / bet_other / collect / statistic -->
          <div class="chat-filters">
            <button type="button" :class="{ checked: chatShow.bet_me }" @click="toggleChatFilter('bet_me')">我的投注</button>
            <button type="button" :class="{ checked: chatShow.bet_other }" @click="toggleChatFilter('bet_other')">他人投注</button>
            <button type="button" :class="{ checked: chatShow.collect }" @click="toggleChatFilter('collect')">开奖</button>
            <button type="button" :class="{ checked: chatShow.close_check }" @click="toggleChatFilter('close_check')">封盘列表</button>
            <button type="button" :class="{ checked: chatShow.statistic }" @click="toggleChatFilter('statistic')">报表</button>
          </div>
          <div class="chat-send">
            <input v-model="chatContent" type="text" maxlength="1000" placeholder="请输入您要发送的内容" @keyup.enter="sendChatMessage">
            <button type="button" :disabled="!chatContent.trim()" @click="sendChatMessage">发送</button>
          </div>
        </footer>
      </section>

      <!--
        切到聊天室时用 v-show（display:none）隐藏下注区，DOM 和组件状态全部保留、
        不卸载 —— 来回切 tab 后台面上的筹码、盘口金额、开奖牌面都还在。
        内部结构一行未改。

        || !chatEnabled 是兜底：后台把「聊天室」开关关掉时 chatEnabled 会转成
        false、浮层随之被 v-if 摘掉，若此时 playType 还停在 'chat'，下注区会跟着
        一起隐藏而整屏空白。加上这个条件后聊天室不可用时下注区一定可见。
      -->
      <div v-show="playType === 'play' || !chatEnabled" class="bet-main">
        <div class="user">
          <div v-if="user" class="info">
            <div class="avatar"><img :src="userAvatar" alt="" @error="onAvatarError" /></div>
            <div class="nick-name"> {{ user.username }} </div>
            <div class="money-img"><img src="/legacy/images/game/paijiu/layout/money.png" alt="" /></div>
            <div class="user-money"> {{ fmt2(user.balance) }} </div>
            <!-- 旧版这里是 ion-icon[name=cloud-done]，没有 Ionic 就用同形状字符 -->
            <div class="user-online"><span class="cloud-icon">☁</span> {{ online }} </div>
            <div class="info-last"></div>
          </div>
          <div class="audio" @click="toggleAudio">
            <img v-if="audioOn" class="audio" src="/legacy/images/game/paijiu/layout/audio_on.png" alt="声音已开" />
            <img v-else class="audio" src="/legacy/images/game/paijiu/layout/audio_off.png" alt="声音已关" />
          </div>
          <div class="action" @click="showMenuEvent"><img class="menu" src="/legacy/images/game/paijiu/layout/menu.png" alt="菜单" /></div>
        </div>

        <div class="today">
          <span class="today-text"> 今日输赢： </span>
          <span class="today-value" :class="{ win: todayProfit > 0, lost: todayProfit < 0 }"> {{ fmt2(todayProfit) }} </span>
        </div>

        <div class="total-bet">
          <span class="total-bet-text">总投注：</span>
          <span class="total-bet-value">{{ fmt2(currentBetTotal) }}</span>
        </div>

        <div class="time">
          <div class="time-tip"> 第 <span class="game-no"> {{ gameState.game_no }} </span> 期 </div>
          <div class="time-lottery-time">
            <!-- 旧版 node 47：lottery_time > 0 才显示秒数，红色条件是 <= game_lottery_time -->
            <template v-if="showCountdown">
              <span class="time-jump" :class="{ red: countdownRed }"> {{ gameState.lottery_time }} </span>
              <span class="time-text"> 秒 </span>
            </template>
            <!-- 旧版 node 49：lottery_time <= 0 显示「开奖中」 -->
            <span v-else class="lottery-ing"> 开奖中 </span>
          </div>
        </div>

        <div class="bet-items">
          <!--
            旧版这里是五个硬编码的 div.bet-item（上排 a/b/c 标题 1/2/3，
            下排先 e 标题 5 再 d 标题 4），这里用固定顺序数组渲染，输出的 DOM 一致。
            .bet-item 是 div 而不是 button —— button 里不允许放 div。
          -->
          <div class="bet-items-first">
            <div
              v-for="key in firstRow"
              :key="key"
              :class="['bet-item', key, { selected: bettingOpen && selectedGate === key }]"
              role="button"
              tabindex="0"
              @click="selectGate(key, $event)"
            >
              <!-- 旧版遮罩条件：betItems[x].close && !lottery -->
              <div v-if="betItems[key].close && !lottery" class="bet-close"><div class="text">封盘</div></div>
              <button v-if="!lottery && Number(betItems[key].bet.member) > 0" class="gate-detail-button" type="button" @click.stop="openGateDetail(key)">详细</button>
              <div class="bet-content">
                <div class="play-title"> {{ gateLabel[key] }} </div>
                <div class="bet-info">
                  <span class="bet-total-all"> {{ fmt0(betItems[key].bet.total) }} </span>
                  <span class="bet-total-member"> {{ fmt0(betItems[key].bet.member) }} </span>
                  <span v-if="bettingOpen && pendingBetAmount(key)" class="bet-total-pending">待确认 {{ fmt0(pendingBetAmount(key)) }}</span>
                </div>
                <!-- 旧版牌面条件是全局 lottery，不是每门自己的 -->
                <div v-if="lottery" class="lottery-item">
                  <div v-if="isOzbPj" class="lottery-item-span"><img :src="ozbpjLotteryTile(betItems[key].lottery.sum)" alt="" /></div>
                  <div v-else v-for="(card, index) in betItems[key].lottery.item" :key="index" class="lottery-item-span"><img :src="`/legacy/images/game/paijiu/lottery/${card}.png`" alt="" /></div>
                </div>
                <div v-if="lottery" class="lottery-frame-point">{{ betItems[key].lottery.sum }} 点</div>
                <div class="bet-lists">
                  <!-- 旧版每个筹码外层有 ngIf money > 0 -->
                  <template v-for="(chip, index) in betItems[key].bet.lists" :key="`${key}-${index}`">
                    <div v-if="chip.money > 0" class="bet-lists-icon" :style="{ left: chip.x, top: chip.y }">
                      <img v-if="[10, 50, 100, 500, 1000].includes(Number(chip.money))" :src="`/legacy/images/game/paijiu/bet/${chip.money}.png`" alt="" />
                      <span v-else class="custom-bet-chip">{{ fmt0(chip.money) }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="bet-items-second">
            <div
              v-for="key in secondRow"
              :key="key"
              :class="['bet-item', key, { selected: bettingOpen && selectedGate === key }]"
              role="button"
              tabindex="0"
              @click="selectGate(key, $event)"
            >
              <div v-if="betItems[key].close && !lottery" class="bet-close"><div class="text">封盘</div></div>
              <button v-if="!lottery && Number(betItems[key].bet.member) > 0" class="gate-detail-button" type="button" @click.stop="openGateDetail(key)">详细</button>
              <div class="bet-content">
                <div class="play-title"> {{ gateLabel[key] }} </div>
                <div class="bet-info">
                  <span class="bet-total-all"> {{ fmt0(betItems[key].bet.total) }} </span>
                  <span class="bet-total-member"> {{ fmt0(betItems[key].bet.member) }} </span>
                  <span v-if="bettingOpen && pendingBetAmount(key)" class="bet-total-pending">待确认 {{ fmt0(pendingBetAmount(key)) }}</span>
                </div>
                <div v-if="lottery" class="lottery-item">
                  <div v-if="isOzbPj" class="lottery-item-span"><img :src="ozbpjLotteryTile(betItems[key].lottery.sum)" alt="" /></div>
                  <div v-else v-for="(card, index) in betItems[key].lottery.item" :key="index" class="lottery-item-span"><img :src="`/legacy/images/game/paijiu/lottery/${card}.png`" alt="" /></div>
                </div>
                <div v-if="lottery" class="lottery-frame-point">{{ betItems[key].lottery.sum }} 点</div>
                <div class="bet-lists">
                  <template v-for="(chip, index) in betItems[key].bet.lists" :key="`${key}-${index}`">
                    <div v-if="chip.money > 0" class="bet-lists-icon" :style="{ left: chip.x, top: chip.y }">
                      <img v-if="[10, 50, 100, 500, 1000].includes(Number(chip.money))" :src="`/legacy/images/game/paijiu/bet/${chip.money}.png`" alt="" />
                      <span v-else class="custom-bet-chip">{{ fmt0(chip.money) }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="bet-actions" aria-label="待确认下注操作">
            <button type="button" :disabled="!bettingOpen || !selectedGate || isConfirmingBets" @click="cancelPendingBet">取消</button>
            <button type="button" :disabled="!bettingOpen || !selectedGate || isConfirmingBets" @click="betAllIn">梭哈</button>
            <input
              v-model="customBetAmount"
              class="custom-bet-input"
              type="number"
              min="0"
              step="0.01"
              inputmode="decimal"
              :disabled="!bettingOpen || !selectedGate || isConfirmingBets"
              placeholder="请自定义输入金额"
              aria-label="请自定义输入金额"
              @input="syncCustomBet"
            />
            <button type="button" :disabled="!bettingOpen || !pendingBetTotal || isConfirmingBets" @click="clearPendingBets">清空</button>
            <button type="button" class="confirm" :disabled="!bettingOpen || !pendingBetTotal || isConfirmingBets" @click="confirmPendingBets">确定</button>
          </div>
        </div>
      </div>

      <!-- 筹码栏跟着下注区一起 v-show，聊天室占满 pk-header 以下的全部空间 -->
      <div v-show="playType === 'play' || !chatEnabled" class="pk-footer">
        <div class="bg"></div>
        <div class="bet-icon">
          <!-- 旧版五颗筹码是硬编码的 10/50/100/500/1000 -->
          <div class="bet-money-button" :class="{ 'bet-money-checked': bettingOpen && betMoney === 10 }" @click="changBetMoney(10)"><img src="/legacy/images/game/paijiu/bet/10.png" alt="10" /></div>
          <div class="bet-money-button" :class="{ 'bet-money-checked': bettingOpen && betMoney === 50 }" @click="changBetMoney(50)"><img src="/legacy/images/game/paijiu/bet/50.png" alt="50" /></div>
          <div class="bet-money-button" :class="{ 'bet-money-checked': bettingOpen && betMoney === 100 }" @click="changBetMoney(100)"><img src="/legacy/images/game/paijiu/bet/100.png" alt="100" /></div>
          <div class="bet-money-button" :class="{ 'bet-money-checked': bettingOpen && betMoney === 500 }" @click="changBetMoney(500)"><img src="/legacy/images/game/paijiu/bet/500.png" alt="500" /></div>
          <div class="bet-money-button" :class="{ 'bet-money-checked': bettingOpen && betMoney === 1000 }" @click="changBetMoney(1000)"><img src="/legacy/images/game/paijiu/bet/1000.png" alt="1000" /></div>
        </div>
      </div>

      <!-- 旧版 node 162：gameCloseState 时盖「未开盘」。它是绝对定位盖在牌桌上的，
           聊天室打开时要一起收起，否则会压在消息流上面。 -->
      <div v-if="gameCloseState && (playType === 'play' || !chatEnabled)" class="game-close-state"><div class="game-close-text">未开盘</div></div>

      <div v-if="gateDetail" class="gate-detail-mask" @click.self="closeGateDetail">
        <section class="gate-detail-dialog" role="dialog" aria-modal="true" :aria-label="`${gateLabel[gateDetail.key]}门下注明细`">
          <header><strong>【{{ gateLabel[gateDetail.key] }}门总投：{{ fmt2(gateDetail.total_money) }}】</strong><button type="button" aria-label="关闭" @click="closeGateDetail">×</button></header>
          <div class="gate-detail-rows">
            <p v-if="gateDetailLoading" class="gate-detail-state">正在加载…</p>
            <p v-else-if="gateDetailError" class="gate-detail-state">{{ gateDetailError }}</p>
            <p v-else-if="!gateDetail.rows.length" class="gate-detail-state">暂无下注</p>
            <p v-else v-for="row in gateDetail.rows" :key="row.bet_id" class="gate-detail-row">
              <span>{{ isOwnGateDetailRow(row) ? '本人' : row.member_name }} - 投：{{ fmt2(row.bet_money) }}</span>
              <BetDetailTime :row="row" :rows="gateDetail.rows" :format-time="chatTimeFull" />
              <button v-if="isOwnGateDetailRow(row)" type="button" :disabled="!canCancelGateDetailRow(row)" @click="requestGateDetailCancel(row)">{{ row.cancel ? '已取消' : '取消' }}</button>
            </p>
          </div>
          <footer><button v-if="chatEnabled" type="button" @click="openGateDetailChat">聊天室</button><button type="button" @click="closeGateDetail">关闭</button></footer>
        </section>
      </div>

      <!-- 旧版 node 164：右侧菜单，[style.top] 跟点击位置 -->
      <div v-if="showMenu.show" class="game-menu" :style="{ top: showMenu.y }">
        <ul>
          <li @click="openRule"><img src="/legacy/images/game/paijiu/layout/rule.png" alt="" /><span>规则</span></li>
          <li @click="openStatisticModal(false)"><img src="/legacy/images/game/paijiu/layout/statistic.png" alt="" /><span>统计</span></li>
          <li @click="goBill"><img src="/legacy/images/game/paijiu/layout/bill.png" alt="" /><span>账单</span></li>
          <li @click="goBets"><img src="/legacy/images/game/paijiu/layout/bet.png" alt="" /><span>下注</span></li>
          <li @click="goHistory"><img src="/legacy/images/game/paijiu/layout/lottery.png" alt="" /><span>开奖</span></li>
          <li @click="goMoney(1)"><img src="/legacy/images/game/paijiu/layout/recharge.png" alt="" /><span>充值</span></li>
          <li @click="goMoney(2)"><img src="/legacy/images/game/paijiu/layout/cash.png" alt="" /><span>提现</span></li>
          <!--
            新增：客服入口。原来在右上角笑脸按钮上，那个位置已改成打开聊天室，
            所以挪到这里。菜单栏只有 50px 宽、字号 .8rem，「联系客服」四个字会
            溢出并把其它项挤歪，因此文字取「客服」，与其余各项的两字排版一致。
          -->
          <li @click="openService"><img src="/legacy/images/game/paijiu/layout/service.png" alt="" /><span>客服</span></li>
        </ul>
      </div>

      <!-- 旧版 node 166：showService && service_wechat_pic 时显示客服二维码。
           代码完整保留，只是触发入口从顶栏笑脸换成了右侧菜单的「客服」。 -->
      <div v-if="showService && serviceWechatPic" class="service">
        <div class="service-img">
          <div class="service-close" @click="showService = false">✕</div>
          <img :src="serviceWechatPic" alt="客服" />
        </div>
      </div>
    </div>

    <!-- 聊天室里撤单前的「请确认」，对齐旧版大吃小的 alert -->
    <div v-if="cancelConfirmTarget" class="chat-cancel-mask">
      <div class="chat-cancel-dialog">
        <h2>请确认</h2>
        <p>您确认取消吗？</p>
        <footer>
          <button type="button" @click="cancelConfirmTarget = null">关闭</button>
          <button type="button" @click="confirmChatCancel">确认</button>
        </footer>
      </div>
    </div>

    <!-- 旧版 ruleModal：全屏页承载 game_rule（后端存的是富文本），版式与「数据统计」一致 -->
    <div v-if="showRule" class="rule-modal">
      <header class="rule-header">
        <button class="rule-back" type="button" aria-label="返回" @click="showRule = false">
          <!-- 与数据统计一致的 Material arrow_back -->
          <svg class="rule-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
        </button>
        <h1 class="rule-title">游戏规则</h1>
        <!-- 右侧等宽占位，抵掉左侧返回按钮的宽度，让标题严格居中 -->
        <span class="rule-spacer" aria-hidden="true"></span>
      </header>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <article class="rule-content" v-html="displayGameName(game.game_rule || '暂无规则说明')"></article>
    </div>

    <!-- 旧版 billModal -->
    <PaijiuBillModal v-if="showBill" :game="game" @close="showBill = false" />

    <!-- 旧版 betModal -->
    <PaijiuBetListModal v-if="showBetList" :game="game" @close="showBetList = false" />

    <!-- 旧版 historyModal -->
    <PaijiuHistoryModal v-if="showHistory" :game="game" @close="showHistory = false" />

    <!-- 旧版 moneyModal：提交成功后刷新余额，对应旧版 dismiss 后页面里的 getUserMoney -->
    <PaijiuMoneyModal
      v-if="moneyModal"
      :game="game"
      :type="moneyModal.type"
      :user="user || { balance: 0 }"
      @close="closeMoneyModal"
    />

    <!-- 旧版 statisticModal -->
    <PaijiuStatisticModal
      v-if="statisticModal"
      :game="game"
      :game-no="statisticModal.gameNo"
      @close="statisticModal = null"
    />

    <!-- 旧版 _wsClose 里 reConnectNumber 到 10 时的 alert -->
    <div v-if="showReconnectAlert" class="reconnect-alert">
      <div class="reconnect-inner">
        <h2>错误</h2>
        <p>您与服务器已断开连接，请重新连接</p>
        <div class="reconnect-actions">
          <button type="button" @click="showReconnectAlert = false">取消</button>
          <button type="button" @click="reconnectSocket">重连</button>
        </div>
      </div>
    </div>

    <p v-if="toastMessage" class="notice">{{ toastMessage }}</p>
  </main>
</template>

<style scoped>
/*
  以下样式逐条对齐旧版 chunk 19 的样式表 J。
  旧版 .content 是 height:100%（确定高度），整条 flex 链靠它才能解析出
  .bet-items-first/.second 的 40%/60% 高度，所以最外层用 100vh 承担同样角色。
*/
.paijiu-page{position:relative;height:100vh;overflow:hidden;color:#fff;font:12px Arial,"Microsoft YaHei",sans-serif}
.content{display:flex;flex-direction:column;height:100%;width:100%;background-image:url('/legacy/images/game/paijiu/layout/bg.png');background-size:100% 100%;background-repeat:no-repeat}

.content .pk-header{min-height:65px;max-height:100px;width:100%;background-image:url('/legacy/images/game/paijiu/layout/head.png');background-size:100% 100%;background-repeat:no-repeat;display:flex;text-align:center}
.content .pk-header .header-left{width:15%;text-align:center}
.content .pk-header .header-left img{width:80%;cursor:pointer}
.content .pk-header .header-center{flex:1}
.content .pk-header .header-center .game-info{color:wheat;height:45%;font-size:.8rem}
.content .pk-header .header-center .game-lottery{display:flex;align-items:center;justify-content:space-around;height:50%}
.content .pk-header .header-center .game-lottery img{height:100%;width:100%}
.ozbpj-number{display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;min-width:24px;height:24px;padding:0 6px;border:1px solid #8ed0ff;border-radius:4px;background:#1479d1;color:#fff;font-weight:700;font-size:15px;line-height:1;text-shadow:0 1px 1px rgb(0 0 0 / 25%)}
.game-lottery .ozbpj-number{flex:0 0 24px;width:24px;height:24px;transform:translateY(-4px);border-color:#5f0d08;border-radius:50%;font-size:15px}
.lottery-item-span .ozbpj-number{width:28px;height:28px;border-radius:50%;font-size:16px}
.content .pk-header .header-right{width:15%;text-align:center}
.content .pk-header .header-right img{width:80%;cursor:pointer}

.content .bet-main{flex:1;display:flex;flex-direction:column;margin-top:10px;position:relative;}
.content .bet-main .user{display:flex;font-size:.8rem;align-items:center}
.content .bet-main .user .info{flex:1;display:flex;justify-content:flex-start;align-items:center}
.content .bet-main .user .info div{display:block;padding-left:5px;padding-right:5px;border:1px solid #ff0;border-left:none;border-right:none;line-height:35px;height:35px;background:rgb(0 0 0 / 20%);color:#fff}
/* 头像 div：独立圆形容器，覆盖通用 div 样式 */
.content .bet-main .user .info div.avatar{padding:0;width:35px;height:35px;line-height:normal;overflow:hidden;background:transparent;display:flex;align-items:center;justify-content:center}
.content .bet-main .user .info div.avatar img{width:35px;height:35px;border-radius:50%;object-fit:cover;margin:0;display:block}
.content .bet-main .user .info div.info-last{border-right:1px solid #ff0;width:35px;border-top-right-radius:50%;border-bottom-right-radius:50%}
.content .bet-main .user .info div.user-money{color:#ff0}
.content .bet-main .user .info img{margin-top:5px;width:25px;height:25px}
/* 旧版 .audio 只有 25px 宽，icon 颜色 #ffe25d，margin-right:10px */
/* 原图 audio_on/off.png 是 50x73 的竖长图，只能定宽、高度自适应，写死正方形会拉变形 */
.content .bet-main .user .audio{width:25px;text-align:center;margin-right:8px;cursor:pointer}
.content .bet-main .user .audio img{width:25px;height:auto;display:block}
/* 旧版 .action{width:15%}，里面 img.bill{width:80%}（旧版有个 .bill class） */
/* menu.png 是 131x127，同样定宽、高度自适应 */
.content .bet-main .user .action{width:48px;text-align:center;cursor:pointer}
.content .bet-main .user .action img{width:46px;height:auto;display:block;margin:0 auto}
.content .bet-main .user .info .cloud-icon{color:#ffe25d}

/* top 要大于 .user 行实际高度（受 .action img 44px 撑开），否则会贴上去 */
.content .bet-main .today{position:absolute;top:50px;left:5px;font-size:.8rem;z-index:1}
.content .bet-main .today .today-text{color:#fff;display:block}
/* margin-top 拉开与「今日输赢:」的行间距 */
.content .bet-main .today .today-value{display:block;color:#fff;margin-top:4px}
.content .bet-main .today .today-value.win{color:red}
.content .bet-main .today .today-value.lost{color:#ffe25d}
.content .bet-main .total-bet{position:absolute;top:50px;right:16px;font-size:.8rem;z-index:1;text-align:left}
.content .bet-main .total-bet span{display:block}
.content .bet-main .total-bet .total-bet-text{color:#fff}
.content .bet-main .total-bet .total-bet-value{margin-top:4px;color:#ffe25d}

.content .bet-main .time{color:wheat;text-align:center;padding-top:10px;padding-bottom:10px}
.content .bet-main .time .time-tip{font-size:1.2rem;height:30px;line-height:30px}
.content .bet-main .time .time-tip .game-no{color:#3bc8d0}
.content .bet-main .time .time-lottery-time{font-size:12px;height:60px;line-height:60px}
.content .bet-main .time .time-lottery-time .time-jump{font-size:2.5rem;color:#fff}
.content .bet-main .time .time-lottery-time .time-jump.red{color:red}
.content .bet-main .time .time-lottery-time .time-text{font-size:12px}

/*
  台面。关键：.bet-items 只能有 flex:1，不能加 display:flex / min-height:0 ——
  子元素的 height:40%/60% 需要父级是确定高度才能解析，加了 min-height:0
  会让它收缩到 0，门框就塌成一条线。门宽也是旧版的固定百分比，不是 flex:1。
*/
.bet-items{flex:1}
.bet-items-first{height:40%;width:100%;display:flex}
.bet-items-first .bet-item{position:relative;width:33%;margin:5px;border:1px solid #5fc988;border-radius:5px;cursor:pointer}
/* 4、5 门底部固定留出 40px，避免台面边框贴近下注筹码栏。 */
.bet-items-second{height:calc(60% - 40px);width:100%;display:flex}
.bet-items-second .bet-item{position:relative;width:50%;margin:5px;border:1px solid #5fc988;border-radius:4px;cursor:pointer}
.bet-item.selected{border-color:#ecff40;box-shadow:0 0 5px rgb(236 255 64 / 55%)}
.bet-items-second .bet-item.e,.bet-items-second .bet-item.e .bet-close{border-bottom-left-radius:30%}
.bet-items-second .bet-item.d,.bet-items-second .bet-item.d .bet-close{border-bottom-right-radius:30%}
.bet-actions{height:40px;display:flex;align-items:center;gap:7px;padding:0 4%;box-sizing:border-box}
.bet-actions button{flex:0 0 auto;width:min(64px,16vw);min-width:0;border:1px solid #ecff40;border-radius:6px;padding:6px 5px;background:#08763f;color:#ecff40;font:700 14px Arial,"Microsoft YaHei",sans-serif;line-height:1.1;cursor:pointer}
.custom-bet-input{flex:1;min-width:0;height:30px;border:1px solid #ecff40;border-radius:6px;padding:0 8px;background:rgb(0 0 0 / 20%);color:#fff;font:13px Arial,"Microsoft YaHei",sans-serif;outline:none;box-sizing:border-box}
.custom-bet-input::placeholder{color:rgb(255 255 255 / 72%)}
.custom-bet-input:focus{border-color:#fff;box-shadow:0 0 0 1px rgb(236 255 64 / 45%)}
.custom-bet-input:disabled{border-color:#719578;background:#2d6048;color:#b9ccbc}
.bet-actions button.confirm{background:#7f0000;color:#ecff40;border-color:#ecff40}
.bet-actions button:not(:disabled):active{background:#076a38}
.bet-actions button:disabled{border-color:#719578;background:#2d6048;color:#b9ccbc;cursor:default}
.bet-item .bet-close{z-index:10;position:absolute;top:0;left:0;width:100%;height:100%;background-color:rgb(0 0 0 / 40%);border-radius:5px;display:flex;flex-direction:column;align-items:center;justify-content:center}
.bet-item .bet-close .text{flex:1;position:absolute;width:100px;height:30px;top:50%;left:50%;margin-left:-50px;margin-top:-15px;text-align:center;line-height:30px;color:#fff}
.bet-content{z-index:9;position:absolute;height:100%;width:100%}
.bet-content .play-title{display:inline-block;position:absolute;top:0;left:5px;font-size:45px;color:#7ae2a2}
.gate-detail-button{position:absolute;z-index:11;top:7px;right:6px;border:1px solid rgb(255 226 93 / 85%);border-radius:3px;padding:2px 5px;background:rgb(0 0 0 / 50%);color:#ffe25d;font:12px Arial,"Microsoft YaHei",sans-serif;line-height:1.2;cursor:pointer}
.bet-content .bet-info{z-index:7;display:inline-block;position:absolute;top:8px;left:35px;font-size:14px;color:#fff}
.bet-content .bet-info span{display:block}
.bet-content .bet-info .bet-total-all{color:#fff}
.bet-content .bet-info .bet-total-member{color:#ffe25d}
.bet-content .bet-info .bet-total-pending{color:#ffdb63;font-size:11px;white-space:nowrap}
.bet-content .lottery-item{z-index:8;position:absolute;left:5px;top:55px;display:flex;align-items:center}
.bet-content .lottery-item .lottery-item-span{width:40px}
.bet-content .lottery-item .lottery-item-span img{width:100%}
.bet-content .lottery-frame-point{z-index:8;position:absolute;bottom:8px;left:50%;transform:translateX(-50%);color:#fff;font-size:16px;font-weight:700;line-height:1;white-space:nowrap}
.bet-content .bet-lists .bet-lists-icon{display:inline-block;position:fixed}
.bet-content .bet-lists .bet-lists-icon img{width:30px}
.custom-bet-chip{display:flex;align-items:center;justify-content:center;width:30px;height:30px;border:2px solid #ffe25d;border-radius:50%;background:#0b8850;color:#fff;font-size:9px;font-weight:700;line-height:1;text-align:center;overflow:hidden}

.content .pk-footer{min-height:100px;max-height:150px}
.content .pk-footer .bg{background-image:url('/legacy/images/game/paijiu/layout/foot.png');background-size:100% 100%;background-repeat:no-repeat;position:absolute;bottom:0;left:0;min-height:65px;max-height:150px;width:100%;height:100px}
.bet-icon{width:100%;position:absolute;bottom:10px;left:0;display:flex;justify-content:center;align-items:center}
.bet-money-button{width:20%;text-align:center;cursor:pointer}
.bet-money-button img{width:70px;max-width:100%}
.bet-money-button.bet-money-checked img{width:75px;height:75px;border-radius:50%;box-shadow:0 0 4px 4px rgb(239 222 103 / 80%)}

/* 旧版「未开盘」遮罩 */
.game-close-state{z-index:15;position:absolute;top:65px;left:0;width:100%;height:100%;background-color:rgb(0 0 0 / 40%)}
.game-close-state .game-close-text{position:absolute;width:100px;height:30px;top:50%;left:50%;margin-left:-50px;margin-top:-15px;text-align:center;line-height:30px;color:#fff}

/* 旧版右侧菜单 */
.game-menu{position:absolute;z-index:14;width:50px;right:0}
.game-menu ul{margin:0;padding:0}
.game-menu ul li{margin:5px 0;list-style:none;color:#fff;text-align:center;font-size:.8rem;position:relative;cursor:pointer}
.game-menu ul li img{width:32px}
.game-menu ul li span{display:block;text-align:center;width:100%}

/* 旧版客服二维码弹层 */
.service{position:absolute;top:0;left:0;width:100%;height:100%;z-index:10;text-align:center;display:flex;align-items:center;background-color:rgb(0 0 0 / 40%)}
.service .service-img{position:relative;width:80vw;margin:0 auto}
.service .service-img img{width:100%}
.service .service-img .service-close{position:absolute;right:-22px;top:-20px;color:red;font-size:1.5rem;cursor:pointer}

/*
  规则页（旧版是 Ionic modal，占满全屏）。版式与 PaijiuStatisticModal 的顶栏保持一致：
  红底白字（主题色 --legacy-theme #f95454）、56px 高、Material arrow_back、标题 18px/500、底部投影。
*/
.rule-modal{position:fixed;z-index:25;inset:0;display:flex;flex-direction:column;background:#fff;color:#000;font:14px Arial,"Microsoft YaHei",sans-serif}
.rule-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.rule-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;color:inherit;cursor:pointer;padding:0}
.rule-back .rule-icon{width:24px;height:24px;display:block}
.rule-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:#fff}
.rule-spacer{width:56px;flex:none}
.rule-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:12px;font-size:14px;line-height:1.8;color:#000}
.rule-content :deep(p){margin:0 0 12px}

/* 断线重连提示（旧版 alertCtrl） */
.reconnect-alert{position:fixed;z-index:40;inset:0;display:flex;align-items:center;justify-content:center;background:rgb(0 0 0 / 50%)}
.reconnect-inner{width:70vw;border-radius:10px;padding:16px;background:#fff;color:#333;text-align:center}
.reconnect-inner h2{margin:0 0 8px;font-size:16px}
.reconnect-inner p{margin:0 0 14px;font-size:13px}
.reconnect-actions{display:flex;gap:10px}
.reconnect-actions button{flex:1;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:14px}

.notice{position:fixed;z-index:20;bottom:95px;left:50%;margin:0;transform:translateX(-50%);border-radius:14px;padding:7px 12px;background:rgb(0 0 0 / 60%);color:#fff}
.gate-detail-mask{position:fixed;z-index:30;inset:0;display:flex;align-items:center;justify-content:center;padding:18px;background:rgb(0 0 0 / 55%)}
.gate-detail-dialog{box-sizing:border-box;display:flex;flex-direction:column;width:min(420px,100%);max-height:min(70vh,560px);border:1px solid #ffe25d;border-radius:7px;background:#126d3d;color:#fff;box-shadow:0 4px 16px rgb(0 0 0 / 45%)}
.gate-detail-dialog header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 14px;border-bottom:1px solid rgb(255 255 255 / 28%);font-size:16px}
.gate-detail-dialog header button{border:0;background:transparent;color:#fff;font-size:27px;line-height:20px;cursor:pointer}
.gate-detail-rows{min-height:76px;overflow-y:auto;padding:4px 14px}
.gate-detail-row{display:flex;align-items:center;justify-content:space-between;gap:8px;margin:5px 0;color:#fff;font-size:14px}.gate-detail-row span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.gate-detail-row time{flex:none;color:#ffe25d}.gate-detail-row button{flex:none;border:0;border-radius:2px;padding:2px 8px;background:#f04141;color:#fff;font:12px Arial,"Microsoft YaHei",sans-serif;line-height:18px;cursor:pointer}.gate-detail-row button:disabled{background:#6d8a79;color:#d3ded6;cursor:default}
.gate-detail-state{margin:22px 0;text-align:center;color:#d8f3df}
.gate-detail-dialog footer{display:flex;justify-content:space-between;gap:12px;padding:7px 14px;border-top:1px solid rgb(255 255 255 / 28%)}.gate-detail-dialog footer button{min-width:96px;border:1px solid #ffe25d;border-radius:3px;padding:5px 18px;background:#1a8f4e;color:#fff;font:14px Arial,"Microsoft YaHei",sans-serif;cursor:pointer}
@media (min-width:641px){.paijiu-page{max-width:640px;margin:auto;box-shadow:0 0 20px rgb(0 0 0 / 50%)}}

/* ===========================================================================
   聊天室（新增）。取值逐条照搬旧版大吃小 chunk 29 的样式表，只是把类名前缀
   从 .pjx- 换成 .chat-，并按牌九的五门两张牌重排开奖公告卡片。

   .paijiu-page 整页字色是 #fff（牌桌是深绿底），聊天室是浅底，所以容器上
   重新指定 color 与 font-size（旧版 .chat-content 是 .8rem）。
   =========================================================================== */
.paijiu-chat{flex:1;min-height:0;display:flex;flex-direction:column;background:#f5f5f5;color:#111;font-size:.8rem;text-align:left;margin-top:-5px}

/* 旧版 .play-type：容器 flex + padding-bottom .2rem + 1px #e5e5e5 底线；
   内层 span font-size .8rem / inline-block / padding 5px 20px；
   选中态给按钮上色、给内层 span 加 2px 下边框。红主题色 #f95454。 */
.chat-tabs{display:flex;align-items:center;padding-bottom:.2rem;border-bottom:1px solid #e5e5e5;background:#fff;font-size:1rem}
.chat-tabs button{width:100%;border:0;background:transparent;padding:5px;color:inherit;font:inherit;text-align:center;cursor:pointer}
.chat-tabs button>span{display:inline-block;padding:5px 20px;font-size:.8rem}
.chat-tabs .active{color:#f95454}
.chat-tabs .active>span{border-bottom:2px solid #f95454}
.chat-tabs .time{padding:0}
.chat-tabs b{display:inline-grid;width:16px;height:19px;margin-left:4px;place-items:center;border-radius:4px;background:red;color:#fff;font-size:12px}

/* 旧版 .account-strip 金额条 */
.chat-account-strip{display:flex;justify-content:space-between;padding:5px 12px;background:#d9f8fc;color:#333;font-size:12px}
.chat-account-strip b{color:#f04141;font-weight:400}
.chat-account-strip em{color:#777;font-style:normal}

/* 消息流 */
.chat-container{flex:1;min-height:0;overflow-y:auto;padding:0 15px}
.chat-msg-item{margin:8px 0}
.chat-msg-time{text-align:center;color:#ccc;font-size:11px}
.chat-message{display:flex;align-items:flex-start}
.chat-me{justify-content:flex-end}
.chat-other{justify-content:flex-start}
.chat-avatar{width:32px;height:32px;flex:0 0 32px;object-fit:cover}
/* 自己的消息头像排到右侧 */
.chat-me .chat-avatar{order:2}
.chat-msg-content{position:relative;max-width:82%;margin:0 8px}
/*
  昵称行（方案 A）：昵称 + 发送时间。
  DOM 顺序固定是 [昵称, 时间]，靠 flex-direction 做左右对称 ——
  他人气泡 row，渲染成「昵称 时间」并靠左；自己的气泡 row-reverse，
  主轴起点翻到右边，渲染成「时间 昵称」并靠右。
  两种情况下昵称都紧贴自己那侧的头像，时间都在远离头像的一侧。
*/
.chat-nickname{display:flex;align-items:baseline;gap:6px;margin:0 0 2px;color:#666}
.chat-me .chat-nickname{flex-direction:row-reverse}
/* 昵称过长时省略，保证时间不被挤出这一行（时间是 HH:mm:ss，占位比昵称固定） */
.chat-nickname span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
/* 时间比昵称再小一档、更浅，避免抢注单信息的注意力 */
.chat-nickname time{flex:0 0 auto;color:#b3b3b3;font-size:11px}
.chat-msg-text,.chat-bet-msg{margin:0;border-radius:3px;background:#fff;color:#111}
.chat-msg-text{padding:7px 12px;line-height:18px;word-break:break-word}
.chat-me .chat-msg-text,.chat-me .chat-bet-msg{background:#9eea6a}

/* 注单气泡：左边是逐条注单，右边是取消/跟投按钮 */
.chat-bet-msg>p{margin:0;padding:6px 12px;border-bottom:1px solid #e5e5e5}
.chat-bet-msg>p b{color:#f04141}
.chat-bet-detail{display:flex;align-items:center}
.chat-bet-lines{min-width:0;flex:1}
.chat-bet-line{display:flex;align-items:center;gap:8px;padding:6px 12px 6px 12px}
.chat-bet-line span{flex:1}
.chat-bet-line b{color:#f04141}
.chat-bet-action{flex:0 0 auto;padding:0 12px}
.chat-bet-action button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:5px 11px;cursor:pointer}
.chat-bet-action button:disabled{background:#ccc;cursor:default}

/* 开奖公告 / 报表卡片，旧版 .msg-card：80% 宽居中、绿底白字 */
.chat-msg-card{box-sizing:border-box;width:80%;margin:auto;border:1px solid #e5e5e5;border-radius:2px;background:#2fbc8b;color:#fff;text-align:center}
.chat-msg-card strong{display:block;padding:2px;border-bottom:1px solid #e5e5e5}

/*
  开奖公告（方案 A）：五门横排，每门三行 —— 门号 / 两个彩色号码球 / 「N点」。
  卡片只有 80% 宽要塞 5 门 × 2 球，所以球压到 22px 并把 gap 收紧。

  点数用纯白而不是牌桌上那种红色：卡片底色是 #2fbc8b，红字（#ff3b30）在上面
  对比度只有约 1.45:1，实测发暗发糊；白字约 2.44:1，且与同一张卡片里的门号、
  标题统一。改白后不再需要原先那层用来救红字的 text-shadow。
*/
.chat-collect-gates{display:grid;grid-template-columns:repeat(5,1fr);gap:3px;padding:6px 4px}
.collect-gate{display:grid;justify-items:center;gap:3px}
.collect-gate.position-gate{gap:7px}
.collect-gate.position-gate .collect-gate-point{display:grid;min-width:30px;height:22px;place-items:center;background:#05592a;color:#fff;font-size:12px;line-height:1;font-weight: 700;}
.collect-gate-label{white-space:nowrap;font-weight: 700}
.collect-gate-balls{display:flex;gap:2px}
.collect-gate-balls i{display:grid;width:22px;height:22px;place-items:center;border-radius:2px;color:#fff;font-size:11px;font-style:normal}
.collect-gate-point{color:#fff;white-space:nowrap}

/* 报表卡片表格 */
.chat-stat-head,.chat-stat-row{display:grid;grid-template-columns:1.2fr 1fr 1fr 1fr;padding:4px 9px;line-height:18px}
.chat-stat-money{color:#f04141}
.chat-stat-loss{color:#fff100}

.chat-close-check-card{width:80%;max-width:520px;background:#1a8f4e;color:#fff;text-align:left}
.chat-close-check-card>strong{border-bottom:0}
.chat-close-check-card>strong,.close-check-total{text-align:center}
.close-check-total{margin:0;padding:2px;border-bottom:1px solid #e5e5e5}
.close-check-gate{padding:6px 10px;border-bottom:1px dashed rgb(255 255 255 / 45%)}
.close-check-gate:last-child{border-bottom:0}
.close-check-gate>b{display:block}
.close-check-none{margin:4px 0 0;text-align:center}
.close-check-row{display:flex;justify-content:space-between;gap:8px;margin:4px 0 0;color:#fff}
.close-check-row span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.close-check-row time{flex:0 0 auto}
.gate-detail-row .bet-detail-time{color:#ffe25d}

/* 底部：四个筛选 + 输入框 */
.chat-footer{background:#fff}
.chat-filters{display:flex;justify-content:space-around;padding:5px;background:#f5f5f5}
.chat-filters button{border:1px dashed #ccc;background:transparent;color:#999;padding:2px 5px;font-size:11px;cursor:pointer}
.chat-filters button.checked{border-color:#f04141;color:#f04141}
.chat-send{display:flex;gap:8px;padding:12px 15px}
.chat-send input{min-width:0;flex:1;border:0;border-radius:3px;background:#f5f5f5;padding:8px;color:#111;font-size:.8rem;outline:none}
.chat-send button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:0 16px;cursor:pointer}
.chat-send button:disabled{background:#ccc;cursor:default}

/*
  号码球十色，直接复用大吃小的 palette 色值（见 script 里的 palette / color()）。

  必须带 .paijiu-chat 前缀限定作用域：牌桌上的倒计时数字用的是
  :class="{ red: countdownRed }"（样式 .time-jump.red{color:red}），
  如果这里写成无前缀的 .red{background:#ef2b16}，封盘前那个大号秒数
  会被套上一块红色背景。
*/
.paijiu-chat .olive{background:#d8dc00}
.paijiu-chat .blue{background:#008ed4}
.paijiu-chat .black{background:#343434}
.paijiu-chat .orange{background:#f06a00}
.paijiu-chat .teal{background:#0ccbd1}
.paijiu-chat .purple{background:#4c2ee8}
.paijiu-chat .gray{background:#b5b7b7}
.paijiu-chat .red{background:#ef2b16}
.paijiu-chat .brown{background:#850a03}
.paijiu-chat .green{background:#00a90d}

/* 撤单确认（旧版 alertCtrl） */
.chat-cancel-mask{position:fixed;inset:0;z-index:80;display:grid;place-items:center;background:#0005}
.chat-cancel-dialog{width:250px;background:#fff;color:#333}
.chat-cancel-dialog h2{margin:0;padding:18px 22px 10px;font-size:18px}
.chat-cancel-dialog p{margin:0;padding:0 22px 25px;color:#888}
.chat-cancel-dialog footer{display:flex;justify-content:flex-end;gap:24px;padding:10px 22px 16px}
.chat-cancel-dialog button{border:0;background:transparent;color:#f04141;cursor:pointer}
</style>
